import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Shield, Target, RefreshCw, BarChart3, List,
  FileDown, Presentation, Table, FileJson, FileType,
  CheckCircle2, Clock, AlertTriangle, ChevronRight,
  Loader2, Eye, Download, Calendar, Globe, Bug,
  ExternalLink, Info, Brain, Sparkles, Bot, Lightbulb,
  Play, Search, Filter, Zap,
  ChevronDown, ChevronUp, Activity, Users, Network,
  Key, Crown, Lock, Database, Wand2, FileCode,
  PieChart, TrendingUp, AlertCircle, Server, X, FolderOpen,
  ChevronLeft, Image, Type, Layout,
  ShieldCheck, ClipboardList, Cog, FileCheck, RotateCcw, Building2,
  Wrench, Hammer, Table2, Crosshair, BookOpen,
  ShieldAlert, Layers, FileSpreadsheet
} from 'lucide-react';
import { ReportsWelcomeBanner } from '../../components/WelcomeBanner';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import PlanRestrictedPage from '../../components/PlanRestrictedPage';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import { useLicense } from '../../hooks/useLicense';
import LicenseBlock from '../../components/LicenseBlock';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import RiskCartography from '../../components/RiskCartography';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Types de rapports disponibles
const REPORT_TYPES = [
  {
    id: 'executive',
    name: 'Executive Report',
    subtitle: 'Management / COMEX',
    description: 'Strategic summary with key indicators, global risk score and priority recommendations. Business language.',
    icon: BarChart3,
    color: 'bg-blue-500',
    preview: '/previews/executive.png'
  },
  {
    id: 'technical',
    name: 'Detailed Technical Report',
    subtitle: 'Security / Infra / Dev Team',
    description: 'Complete vulnerability analysis with technical details, CVE, CVSS, evidence and remediation recommendations.',
    icon: Shield,
    color: 'bg-green-500',
    preview: '/previews/technical.png'
  },
  {
    id: 'vulnerability_list',
    name: 'Vulnerability List',
    subtitle: 'Export for tools (Jira, GLPI...)',
    description: 'Structured vulnerability export for import into your ticketing tools and remediation tracking.',
    icon: List,
    color: 'bg-yellow-500',
    preview: '/previews/vulns.png'
  },
  {
    id: 'redteam',
    name: 'Red Team / Attack Report',
    subtitle: 'Attack narrative',
    description: 'Narrative attack scenario with kill chain, MITRE ATT&CK mapping and exploited compromise paths.',
    icon: Target,
    color: 'bg-red-500',
    preview: '/previews/redteam.png'
  },
  {
    id: 'retest',
    name: 'Re-test Report',
    subtitle: 'Verification of fixes',
    description: 'Before/after comparison to verify remediation of previously identified vulnerabilities.',
    icon: RefreshCw,
    color: 'bg-purple-500',
    preview: '/previews/retest.png'
  },
  {
    id: 'multi_campaign',
    name: 'Multi-Campaign Summary',
    subtitle: 'Evolution over time',
    description: 'Consolidated view of multiple scans to analyze security posture evolution over time.',
    icon: BarChart3,
    color: 'bg-cyan-500',
    preview: '/previews/multicampaign.png'
  },
  {
    id: 'active_directory',
    name: 'Active Directory & Identity Report',
    subtitle: 'AD / IAM / Privilege Audit',
    description: 'AD vulnerability analysis, privileged accounts, GPO, dangerous delegations and escalation paths.',
    icon: Users,
    color: 'bg-indigo-500',
    preview: '/previews/ad.png'
  },
  {
    id: 'data_leak',
    name: 'Data Leak / Exposure Report',
    subtitle: 'DLP / Sensitive data',
    description: 'Detection of exposed data, leaked credentials, accessible sensitive files and leak risks.',
    icon: Database,
    color: 'bg-pink-500',
    preview: '/previews/dataleak.png'
  },
  {
    id: 'attack_surface',
    name: 'Rapport Exposition Internet & Surface d\'Attaque',
    subtitle: 'ASM / External perimeter',
    description: 'Mapping of external attack surface, exposed services, subdomains, open ports and risks.',
    icon: Globe,
    color: 'bg-orange-500',
    preview: '/previews/attacksurface.png'
  }
];

// Templates de design disponibles
const DESIGN_TEMPLATES = [
  {
    id: 'blanc',
    name: 'Blanc',
    description: 'Light and bright theme',
    preview: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 50%, #e8e8e8 100%)',
    colors: ['#ffffff', '#f5f5f5', '#e8e8e8'],
    dark: true
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Clean and elegant design',
    preview: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    colors: ['#1a1a2e', '#16213e', '#0f3460']
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Classic business style',
    preview: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #95a5a6 100%)',
    colors: ['#2c3e50', '#34495e', '#95a5a6']
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Contemporary design',
    preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    colors: ['#667eea', '#764ba2', '#f093fb']
  },
  {
    id: 'minimal',
    name: 'Minimalist',
    description: 'Simple and clean',
    preview: 'linear-gradient(135deg, #fafafa 0%, #e0e0e0 50%, #bdbdbd 100%)',
    colors: ['#fafafa', '#e0e0e0', '#bdbdbd'],
    dark: true
  },
  {
    id: 'cyber',
    name: 'Cyber',
    description: 'Cybersecurity style',
    preview: 'linear-gradient(135deg, #0d0d0d 0%, #1a0a2e 50%, #ff0055 100%)',
    colors: ['#0d0d0d', '#1a0a2e', '#ff0055']
  },
  {
    id: 'sylink',
    name: 'SYLink',
    description: 'Official SYLink theme',
    preview: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #00ff88 100%)',
    colors: ['#0a0a0a', '#1a1a1a', '#00ff88']
  }
];

// Formats d'export disponibles
const EXPORT_FORMATS = [
  { id: 'pdf', name: 'PDF', description: 'via Gamma.app', icon: FileDown, extension: '.pdf' },
  { id: 'pptx', name: 'PowerPoint', description: 'via Gamma.app', icon: Presentation, extension: '.pptx' },
  { id: 'csv', name: 'CSV', description: 'Import SIEM/Jira', icon: Table, extension: '.csv' },
  { id: 'txt', name: 'TXT', description: 'Structured text', icon: FileType, extension: '.txt' },
  { id: 'json', name: 'JSON', description: 'API/Import', icon: FileJson, extension: '.json' }
];

// Langues disponibles
const LANGUAGES = [
  { id: 'fr', name: 'French', flag: '🇫🇷' },
  { id: 'en', name: 'English', flag: '🇬🇧' },
  { id: 'es', name: 'Spanish', flag: '🇪🇸' },
  { id: 'de', name: 'German', flag: '🇩🇪' },
  { id: 'it', name: 'Italian', flag: '🇮🇹' },
  { id: 'pt', name: 'Portuguese', flag: '🇵🇹' }
];

// Options Gamma API - Dimensions des slides
const CARD_DIMENSIONS = [
  { id: 'fluid', name: 'Fluid', description: 'Adaptive' },
  { id: '16x9', name: '16:9', description: 'Widescreen' },
  { id: '4x3', name: '4:3', description: 'Standard' }
];

// Options Gamma API - Source des images
const IMAGE_SOURCES = [
  { id: 'aiGenerated', name: 'IA', description: 'AI generated images' },
  { id: 'pictographic', name: 'Icons', description: 'Vector illustrations' },
  { id: 'unsplash', name: 'Unsplash', description: 'Photos HD gratuites' },
  { id: 'noImages', name: 'No images', description: 'Text only' }
];

// Options Gamma API - Mode de texte
const TEXT_MODES = [
  { id: 'generate', name: 'Expand', description: 'Enriches content' },
  { id: 'condense', name: 'Summarize', description: 'Concise version' },
  { id: 'preserve', name: 'Preserve', description: 'Exact text' }
];

// Gamma API options - Text quantity
const TEXT_AMOUNTS = [
  { id: 'brief', name: 'Brief' },
  { id: 'medium', name: 'Medium' },
  { id: 'detailed', name: 'Detailed' }
];

// Options de nombre de pages par type de rapport
// Gamma API supports max 50 cards in detailed mode
const PAGE_COUNT_OPTIONS = {
  executive: { min: 5, max: 15, default: 10, labelKey: 'executive' },
  technical: { min: 10, max: 50, default: 25, labelKey: 'technical' },
  vulnerability_list: { min: 5, max: 30, default: 15, labelKey: 'vulnerability_list' },
  redteam: { min: 10, max: 40, default: 20, labelKey: 'redteam' },
  retest: { min: 5, max: 20, default: 10, labelKey: 'retest' },
  multi_campaign: { min: 10, max: 40, default: 20, labelKey: 'multi_campaign' },
  active_directory: { min: 10, max: 35, default: 20, labelKey: 'active_directory' },
  data_leak: { min: 5, max: 25, default: 15, labelKey: 'data_leak' },
  attack_surface: { min: 10, max: 30, default: 15, labelKey: 'attack_surface' }
};

// Compliance document types (ISO, PSSI, etc.)
const COMPLIANCE_TYPES = [
  {
    id: 'iso27001',
    name: 'ISO 27001',
    description: 'ISO/IEC 27001 compliance plan - Information security management system',
    icon: ShieldCheck,
    color: 'bg-emerald-500',
    formats: ['pdf', 'docx']
  },
  {
    id: 'iso27002',
    name: 'ISO 27002',
    description: 'ISO/IEC 27002 best practices guide - Information security controls',
    icon: ClipboardList,
    color: 'bg-teal-500',
    formats: ['pdf', 'docx']
  },
  {
    id: 'smsi',
    name: 'SMSI',
    description: 'Information Security Management System - Complete documentation',
    icon: Cog,
    color: 'bg-cyan-500',
    formats: ['pdf', 'docx']
  },
  {
    id: 'pssi',
    name: 'PSSI',
    description: 'Information Systems Security Policy - Reference document',
    icon: FileCheck,
    color: 'bg-sky-500',
    formats: ['pdf', 'docx']
  },
  {
    id: 'pca',
    name: 'PCA',
    description: 'Business Continuity Plan - Maintaining critical activities',
    icon: RefreshCw,
    color: 'bg-violet-500',
    formats: ['pdf', 'docx']
  },
  {
    id: 'pra',
    name: 'PRA',
    description: 'Disaster Recovery Plan - Recovery after disaster',
    icon: RotateCcw,
    color: 'bg-purple-500',
    formats: ['pdf', 'docx']
  }
];


const ReportsList = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasFeature, isFreePlan, currentPlan } = usePlanRestrictions();
  const { isBlocked: licenseBlocked } = useLicense();
  const { workspaceId: activeWorkspaceId, workspaces } = useWorkspace();

  // Helper function to get translated report type name
  const getReportTypeName = (typeId) => {
    const key = `reports.types.${typeId}`;
    return t(key);
  };

  // Helper function to get translated format name
  const getFormatName = (formatId) => {
    if (!formatId) return '-';
    const key = `reports.formats.${formatId}`;
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
    return formatId.toUpperCase();
  };
  const [scans, setScans] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedScans, setSelectedScans] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('sylink');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total_scans: 0,
    total_vulns: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    reports_generated: 0
  });

  // AI Assistant state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Generated reports history
  const [generatedReports, setGeneratedReports] = useState([]);

  // Reports list slide panel
  const [showReportsPanel, setShowReportsPanel] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsPage, setReportsPage] = useState(1);
  const REPORTS_PER_PAGE = 20;
  const [expandedReportTypes, setExpandedReportTypes] = useState({});
  const [regeneratingReport, setRegeneratingReport] = useState(null);

  // Gamma API options
  const [cardDimension, setCardDimension] = useState('16x9');
  const [imageSource, setImageSource] = useState('aiGenerated');
  const [textMode, setTextMode] = useState('generate');
  const [textAmount, setTextAmount] = useState('medium');
  const [pageCount, setPageCount] = useState(15);

  // Batch AI Reports generation
  const [generatingAllReports, setGeneratingAllReports] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentReport: '' });
  const [showBatchModal, setShowBatchModal] = useState(false);

  // AI Prompt Report generation
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptResult, setPromptResult] = useState(null);

  // Compliance document state
  const [showComplianceSection, setShowComplianceSection] = useState(false);
  const [selectedComplianceType, setSelectedComplianceType] = useState(null);
  const [complianceFormat, setComplianceFormat] = useState('pdf');
  const [companyName, setCompanyName] = useState('');
  const [generatingCompliance, setGeneratingCompliance] = useState(false);
  const [selectedScanForCompliance, setSelectedScanForCompliance] = useState(null);

  // D2 PCRA state
  const [showPcraSection, setShowPcraSection] = useState(false);
  const [selectedScanForPcra, setSelectedScanForPcra] = useState(null);
  const [pcraFormat, setPcraFormat] = useState('pdf');
  const [pcraOptions, setPcraOptions] = useState({
    useLlm: true,
    includeBia: true,
    includeFichesOp: true,
    includeDmia: true,
    includeIso22301: true,
    includeExercices: false,
  });
  const [generatingPcra, setGeneratingPcra] = useState(false);
  const [pcraProgress, setPcraProgress] = useState(0);

  // Active Directory Reports state
  const [showAdReportSection, setShowAdReportSection] = useState(false);
  const [adMissionName, setAdMissionName] = useState('');
  const [adClientName, setAdClientName] = useState('');
  const [adPingcastleData, setAdPingcastleData] = useState('');
  const [adOradaData, setAdOradaData] = useState('');
  const [adUseLlm, setAdUseLlm] = useState(true);
  const [adFormats, setAdFormats] = useState({ comex_pdf: true, technical_pdf: true, action_plan_excel: true });
  const [generatingAdReport, setGeneratingAdReport] = useState(false);
  const [adTaskId, setAdTaskId] = useState(null);
  const [adProgress, setAdProgress] = useState(0);
  const [adMessage, setAdMessage] = useState('');
  const [adFiles, setAdFiles] = useState(null);
  // Import depuis pentests existants
  const [adSessions, setAdSessions] = useState([]);
  const [adPcScans, setAdPcScans] = useState([]);
  const [loadingAdSessions, setLoadingAdSessions] = useState(false);
  const [selectedAdSource, setSelectedAdSource] = useState(null);
  const [showAdImport, setShowAdImport] = useState(true);

  // Remediation ANSSI E3R state
  const [showRemediationSection, setShowRemediationSection] = useState(false);
  const [selectedScanForRemediation, setSelectedScanForRemediation] = useState(null);
  const [remediationFormat, setRemediationFormat] = useState('pdf');
  const [remediationOptions, setRemediationOptions] = useState({
    useLlm: true,
    includeMitre: true,
    includeProcedures: true,
    includeScripts: true,
    includeHardening: true
  });
  const [generatingRemediation, setGeneratingRemediation] = useState(false);
  const [remediationTaskId, setRemediationTaskId] = useState(null);
  const [remediationProgress, setRemediationProgress] = useState(0);
  const [allScansForRemediation, setAllScansForRemediation] = useState([]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  const fetchData = useCallback(async () => {
    try {
      setFetchError(null);
      setLoading(true);
      const token = localStorage.getItem('access_token');
      // Charger uniquement les scans valides pour les rapports
      // Filter by workspace if active
      let validatedUrl = `/api/v1/scan/validated`;
      if (activeWorkspaceId) {
        validatedUrl += `?workspace_id=${activeWorkspaceId}`;
      }
      const scansResponse = await axios.get(validatedUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const scansData = scansResponse.data.validated_scans || [];
      setScans(scansData);

      const allVulns = [];
      let critCount = 0, highCount = 0, medCount = 0, lowCount = 0;

      for (const scan of scansData) {
        try {
          const vulnsResponse = await axios.get(`/api/v1/scan/${scan.id}/vulnerabilities`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const vulns = vulnsResponse.data || [];
          allVulns.push(...vulns);

          vulns.forEach(v => {
            if (v.severity === 'critical') critCount++;
            else if (v.severity === 'high') highCount++;
            else if (v.severity === 'medium') medCount++;
            else if (v.severity === 'low') lowCount++;
          });
        } catch (e) {}
      }

      setVulnerabilities(allVulns);
      setStats({
        total_scans: scansData.length,
        total_vulns: allVulns.length,
        critical: critCount,
        high: highCount,
        medium: medCount,
        low: lowCount,
        reports_generated: parseInt(localStorage.getItem('reports_count') || '0')
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Check for network errors
      if (error.message?.includes('Network') || error.code === 'ERR_NETWORK' || error.message?.includes('ERR_NETWORK_CHANGED')) {
        setFetchError(t('reports.networkError', 'Network error. Check your connection.'));
      } else if (error.response?.status === 401) {
        setFetchError(t('errors.sessionExpired', 'Session expired. Please log in again.'));
      } else {
        setFetchError(t('reports.loadDataError', 'Error loading data.'));
      }
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch all scans for remediation dropdown (completed or with vulnerabilities)
  useEffect(() => {
    const fetchAllScansForRemediation = async () => {
      setAllScansForRemediation([]);
      try {
        const token = localStorage.getItem('access_token');
        // Get all scans, not just completed - user might want remediation for any scan with vulns
        // Use /api/v1/scan endpoint (not /list) with high limit
        let url = `/api/v1/scan?limit=100`;
        if (activeWorkspaceId) {
          url += `&workspace_id=${activeWorkspaceId}`;
        }
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const scansData = response.data.scans || response.data || [];
        // Filter scans that are completed or have vulnerabilities
        const eligibleScans = scansData.filter(s =>
          s.status === 'completed' ||
          (s.total_vulnerabilities || s.vulns_count || s.vulnerabilities_count || 0) > 0
        );
        console.log('Fetched scans for remediation:', eligibleScans.length, 'from workspace:', activeWorkspaceId);
        setAllScansForRemediation(eligibleScans);
      } catch (error) {
        console.error('Error fetching scans for remediation:', error);
        // Fallback to validated scans if list endpoint fails
        setAllScansForRemediation(scans.filter(s => s.status === 'completed'));
      }
    };
    fetchAllScansForRemediation();
  }, [activeWorkspaceId, scans]);

  // Handle URL params for auto-selection from ScanDetails page
  useEffect(() => {
    const scanIdFromUrl = searchParams.get('scan_id');
    const autoGenerate = searchParams.get('auto_generate') === 'true';

    if (scanIdFromUrl && scans.length > 0 && !loading) {
      // Check if the scan is in the validated scans list
      const scanExists = scans.find(s => s.id === scanIdFromUrl);
      if (scanExists) {
        // Auto-select the scan
        setSelectedScans([scanIdFromUrl]);
        // Auto-select executive report type if auto_generate is true
        if (autoGenerate) {
          setSelectedReportType('executive');
        }
      } else {
        console.warn('Scan not found in validated scans list:', scanIdFromUrl);
      }
      // Clear URL params after handling
      navigate('/reports', { replace: true });
    }
  }, [scans, loading, searchParams, navigate]);

  // Update page count when report type changes
  useEffect(() => {
    if (selectedReportType && PAGE_COUNT_OPTIONS[selectedReportType]) {
      setPageCount(PAGE_COUNT_OPTIONS[selectedReportType].default);
    }
  }, [selectedReportType]);

  // Reload the report library when the active workspace changes
  useEffect(() => {
    loadSavedReports(); // eslint-disable-line
  }, [activeWorkspaceId]); // eslint-disable-line

  // Load the list of generated reports
  const loadSavedReports = async () => {
    setLoadingReports(true);
    setReportsPage(1); // Reset pagination
    try {
      const token = localStorage.getItem('access_token');
      // The library shows all company reports, not just those from the active workspace
      const response = await axios.get('/api/v1/reports/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Trier du plus recent au plus ancien
      const reports = response.data.reports || [];
      reports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setSavedReports(reports);
    } catch (error) {
      console.error('Failed to load saved reports:', error);
      setSavedReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  // Pagination des rapports
  const totalPages = Math.ceil(savedReports.length / REPORTS_PER_PAGE);
  const paginatedReports = savedReports.slice(
    (reportsPage - 1) * REPORTS_PER_PAGE,
    reportsPage * REPORTS_PER_PAGE
  );

  // Telecharger un rapport sauvegarde
  const downloadSavedReport = async (filename, fileType = 'pdf') => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/reports/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Erreur: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('reports.downloadError', 'Download error'));
    }
  };

  // Regenerate a report
  const regenerateReport = async (report) => {
    // Determine format from filename
    const isPdf = report.filename?.endsWith('.pdf');
    const isDocx = report.filename?.endsWith('.docx');
    const isPptx = report.filename?.endsWith('.pptx');
    const format = isPdf ? 'pdf' : isDocx ? 'docx' : isPptx ? 'pptx' : 'pdf';

    // Determine report type
    const reportType = report.type || 'technical';

    // Get scan_id from report or try to extract from filename
    let scanId = report.scan_id;

    // If no scan_id, use the first selected scan or available scan
    if (!scanId && scans.length > 0) {
      const completedScans = scans.filter(s => s.status === 'completed');
      if (completedScans.length > 0) {
        scanId = completedScans[0].id;
      }
    }

    if (!scanId) {
      alert(t('reports.regenerateError', 'Unable to regenerate: no associated scan found'));
      return;
    }

    setRegeneratingReport(report.filename || report.id);

    try {
      const token = localStorage.getItem('access_token');

      // Generate the report
      const response = await fetch(`/api/v1/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scan_ids: [scanId],
          report_type: reportType,
          template: 'professional',
          format: format,
          language: 'fr'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t('reports.regenerateError', 'Error during regeneration'));
      }

      // Reload the reports list
      await loadSavedReports();

      alert(t('success.saved', 'Report regenerated successfully!'));

    } catch (error) {
      console.error('Regeneration failed:', error);
      alert(`Regeneration error: ${error.message}`);
    } finally {
      setRegeneratingReport(null);
    }
  };

  // Ouvrir le panel des rapports
  const openReportsPanel = () => {
    setShowReportsPanel(true);
    loadSavedReports();
  };

  // AI Quick prompts
  const aiQuickPrompts = [
    { icon: FileText, label: t('reports.aiPrompts.executiveSummary'), query: t('reports.aiPrompts.executiveSummaryQuery') },
    { icon: Shield, label: t('reports.aiPrompts.recommendations'), query: t('reports.aiPrompts.recommendationsQuery') },
    { icon: TrendingUp, label: t('reports.aiPrompts.trends'), query: t('reports.aiPrompts.trendsQuery') },
    { icon: AlertCircle, label: t('reports.aiPrompts.criticalRisks'), query: t('reports.aiPrompts.criticalRisksQuery') },
  ];

  // AI Assistant query
  const askAI = async (query) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          query,
          context: {
            scans: scans.slice(0, 10),
            vulnerabilities: vulnerabilities.slice(0, 20),
            stats,
            type: 'report_analysis'
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAiResponse({
          query,
          response: data.response || data.analysis,
          suggestions: data.suggestions || [],
          timestamp: new Date().toISOString()
        });
      } else {
        setAiResponse(generateLocalReportAnalysis(query));
      }
    } catch (err) {
      console.error('AI query failed:', err);
      setAiResponse(generateLocalReportAnalysis(query));
    } finally {
      setAiLoading(false);
      setAiQuery('');
    }
  };

  // Local AI analysis for reports
  const generateLocalReportAnalysis = (query) => {
    let response = '';
    let suggestions = [];
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('executive') || lowerQuery.includes('comex') || lowerQuery.includes('summary')) {
      response = `Executive Summary - Security Analysis\n\n`;
      response += `Analysis period: ${scans.length} scans\n`;
      response += `Total vulnerabilities: ${stats.total_vulns}\n\n`;
      response += `Breakdown by severity:\n`;
      response += `- Critical: ${stats.critical} (${stats.total_vulns > 0 ? Math.round(stats.critical/stats.total_vulns*100) : 0}%)\n`;
      response += `- High: ${stats.high} (${stats.total_vulns > 0 ? Math.round(stats.high/stats.total_vulns*100) : 0}%)\n`;
      response += `- Medium: ${stats.medium}\n`;
      response += `- Low: ${stats.low}\n\n`;
      response += `Global risk score: ${stats.critical > 0 ? 'CRITICAL' : stats.high > 5 ? 'HIGH' : 'MODERATE'}\n\n`;
      response += `Priority actions:\n`;
      response += `1. Fix ${stats.critical} critical vulnerabilities within 48h\n`;
      response += `2. Plan remediation of ${stats.high} high vulnerabilities within 2 weeks\n`;
      response += `3. Update identified obsolete systems`;
      suggestions = ['View critical details', 'Generate PDF report', 'Plan remediation'];
    } else if (lowerQuery.includes('recommendation') || lowerQuery.includes('remediation')) {
      response = `Priority Remediation Recommendations:\n\n`;
      response += `1. IMMEDIATE (0-48h):\n`;
      response += `   - Patch identified critical CVEs\n`;
      response += `   - Isolate most exposed systems\n`;
      response += `   - Strengthen weak authentications\n\n`;
      response += `2. SHORT TERM (1-2 weeks):\n`;
      response += `   - Update vulnerable services\n`;
      response += `   - Configure firewalls\n`;
      response += `   - Harden configurations\n\n`;
      response += `3. MEDIUM TERM (1 month):\n`;
      response += `   - Audit access rights\n`;
      response += `   - Train IT teams\n`;
      response += `   - Implement monitoring`;
      suggestions = ['Export action plan', 'Prioritize by CVSS', 'Create Jira tickets'];
    } else if (lowerQuery.includes('trend') || lowerQuery.includes('evolution')) {
      response = `Security Trends Analysis:\n\n`;
      response += `Scans analyzed: ${stats.total_scans}\n\n`;
      response += `Observations:\n`;
      response += `- Common vulnerability types: Configurations, Authentication, Injection\n`;
      response += `- Most exposed services: Web, SSH, RDP\n`;
      response += `- Fix ratio: To evaluate with retest scans\n\n`;
      response += `Improvement recommendations:\n`;
      response += `- Automate regular scans\n`;
      response += `- Implement CI/CD security\n`;
      response += `- Train developers in secure coding`;
      suggestions = ['Compare periods', 'View metrics', 'Generate trends report'];
    } else if (lowerQuery.includes('critical') || lowerQuery.includes('risk')) {
      response = `Critical Risks Identified:\n\n`;
      response += `Total critical vulnerabilities: ${stats.critical}\n\n`;
      if (stats.critical > 0) {
        response += `Critical risk types:\n`;
        response += `- Remote code execution (RCE)\n`;
        response += `- SQL/Command injection\n`;
        response += `- Authentication bypass\n`;
        response += `- Unauthorized data access\n\n`;
        response += `Potential impact:\n`;
        response += `- Complete system compromise\n`;
        response += `- Sensitive data theft\n`;
        response += `- Lateral movement in network\n`;
        response += `- Ransomware deployment`;
      } else {
        response += `No critical vulnerabilities detected.\n`;
        response += `Continue monitoring and regular scans.`;
      }
      suggestions = ['View critical CVEs', 'Generate emergency report', 'Notify team'];
    } else {
      response = `Pentest Report Analysis:\n\n`;
      response += `Global statistics:\n`;
      response += `- Scans performed: ${stats.total_scans}\n`;
      response += `- Vulnerabilities: ${stats.total_vulns}\n`;
      response += `- Critical: ${stats.critical} | High: ${stats.high}\n`;
      response += `- Medium: ${stats.medium} | Low: ${stats.low}\n\n`;
      response += `Reports generated: ${stats.reports_generated}\n\n`;
      response += `Recommended report types:\n`;
      response += `- Executive: For management and C-suite\n`;
      response += `- Technical: For IT/Security teams\n`;
      response += `- Red Team: Narrative attack scenario`;
      suggestions = ['Executive summary', 'Recommendations', 'Critical risks'];
    }

    return {
      query,
      response,
      suggestions,
      timestamp: new Date().toISOString(),
      isLocal: true
    };
  };

  const toggleScanSelection = (scanId) => {
    setSelectedScans(prev =>
      prev.includes(scanId)
        ? prev.filter(id => id !== scanId)
        : [...prev, scanId]
    );
  };

  const selectAllScans = () => {
    if (selectedScans.length === scans.length) {
      setSelectedScans([]);
    } else {
      setSelectedScans(scans.map(s => s.id));
    }
  };

  // Generate compliance document (ISO 27001, PSSI, etc.)
  const generateComplianceDocument = async () => {
    if (!selectedComplianceType) {
      alert(t('errors.selectRequired', 'Please select a compliance document type'));
      return;
    }

    setGeneratingCompliance(true);
    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`/api/v1/compliance/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_type: selectedComplianceType,
          format: complianceFormat,
          company_name: companyName || 'Organisation',
          scan_id: selectedScanForCompliance,
          include_vulns: !!selectedScanForCompliance
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Download the file directly
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${selectedComplianceType}_${companyName || 'Organisation'}.${complianceFormat}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Update reports count
      const currentCount = parseInt(localStorage.getItem('reports_count') || '0');
      localStorage.setItem('reports_count', (currentCount + 1).toString());
      setStats(prev => ({ ...prev, reports_generated: currentCount + 1 }));

      alert(`${selectedComplianceType.toUpperCase()} compliance document generated successfully!`);
    } catch (error) {
      console.error('Error generating compliance document:', error);
      alert(`${t('reports.generateError', 'Error during generation')} : ${error.message}`);
    } finally {
      setGeneratingCompliance(false);
    }
  };

  // ── Active Directory Reports ───────────────────────────────────────────────
  const generateAdReport = async () => {
    const selectedFormats = Object.entries(adFormats).filter(([,v]) => v).map(([k]) => k);
    if (selectedFormats.length === 0) {
      alert('Please select at least one report format');
      return;
    }
    setGeneratingAdReport(true);
    setAdProgress(0);
    setAdMessage('Launching generation...');
    setAdFiles(null);
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post('/api/v1/ad-reports/generate', {
        mission_name: adMissionName || 'Audit Active Directory',
        client_name: adClientName || 'Client',
        pingcastle_data: adPingcastleData,
        oradad_data: adOradaData,
        formats: selectedFormats,
        use_llm: adUseLlm,
        workspace_id: activeWorkspaceId || null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (resp.data.task_id) {
        setAdTaskId(resp.data.task_id);
        pollAdReportStatus(resp.data.task_id, token);
      }
    } catch (err) {
      console.error('AD report generation error:', err);
      setAdMessage(t('reports.generateError', 'Error during generation'));
      setGeneratingAdReport(false);
    }
  };

  const pollAdReportStatus = async (taskId, token) => {
    const interval = setInterval(async () => {
      try {
        const resp = await axios.get(`/api/v1/ad-reports/status/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { status, progress, message, files } = resp.data;
        setAdProgress(progress || 0);
        setAdMessage(message || '');
        if (status === 'completed') {
          clearInterval(interval);
          setGeneratingAdReport(false);
          setAdFiles(files);
          // Refresh the report library to display the new AD report
          loadSavedReports();
        } else if (status === 'failed') {
          clearInterval(interval);
          setGeneratingAdReport(false);
          setAdMessage(`Erreur : ${message}`);
        }
      } catch (err) {
        clearInterval(interval);
        setGeneratingAdReport(false);
      }
    }, 3000);
  };

  const downloadAdReport = async (format) => {
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.get(`/api/v1/ad-reports/download/${adTaskId}/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const ext = format === 'action_plan_excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ad_report_${format}_${adClientName || 'client'}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('reports.downloadError', 'Download error'));
    }
  };

  // Charger les sessions AD et scans PingCastle quand le bloc est ouvert
  useEffect(() => {
    if (!showAdReportSection) return;
    const load = async () => {
      setLoadingAdSessions(true);
      try {
        const token = localStorage.getItem('access_token');
        const h = { Authorization: `Bearer ${token}` };
        const [s, p] = await Promise.all([
          axios.get('/api/v1/ad-wizard/sessions', { headers: h }).catch(() => ({ data: { sessions: [] } })),
          axios.get('/api/v1/pingcastle/scans', { headers: h }).catch(() => ({ data: { scans: [] } })),
        ]);
        setAdSessions(s.data?.sessions || []);
        setAdPcScans(p.data?.scans || []);
      } finally {
        setLoadingAdSessions(false);
      }
    };
    load();
  }, [showAdReportSection]);

  // Importer depuis une session AD wizard
  const importFromAdSession = async (session) => {
    const token = localStorage.getItem('access_token');
    const domain = session.target_domain || 'domaine.local';
    const clientPart = domain.split('.')[0];
    setAdMissionName(`Audit AD — ${domain}`);
    setAdClientName(clientPart.charAt(0).toUpperCase() + clientPart.slice(1));
    setSelectedAdSource(session.session_id);
    try {
      const resp = await axios.get(`/api/v1/ad-wizard/sessions/${session.session_id}/findings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const findings = resp.data?.findings || [];
      if (findings.length > 0) {
        const lines = findings.slice(0, 40).map(f =>
          `[${f.severity || 'Medium'}] ${f.title || f.name || 'Finding'}\n${f.description || f.evidence || ''}`
        );
        setAdOradaData(
          `=== AD Pentest Results — ${domain} ===\n` +
          `Date: ${(session.started_at || '').split('T')[0]}\n` +
          `Trouvailles: ${session.total_findings || findings.length} | Credentials: ${session.credentials_found || 0}\n\n` +
          lines.join('\n\n')
        );
      }
    } catch (_) {}
  };

  // Importer depuis un scan PingCastle
  const importFromPcScan = async (scan) => {
    const token = localStorage.getItem('access_token');
    const domain = scan.domain || scan.target_domain || 'domaine.local';
    setAdMissionName(`Audit AD PingCastle — ${domain}`);
    setAdClientName(domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1));
    setSelectedAdSource(scan.scan_id);
    try {
      const resp = await axios.get(`/api/v1/pingcastle/scans/${scan.scan_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = resp.data?.result || {};
      const indicators = result.indicators || [];
      const score = result.global_score ?? 'N/A';
      const lines = indicators.slice(0, 60).map(i =>
        `[${i.severity || 'Info'}] ${i.name || i.rule_id || 'Rule'}: ${i.description || ''}`
      );
      setAdPingcastleData(
        `=== Audit PingCastle — ${domain} ===\n` +
        `Score global: ${score}/100\n` +
        `Date: ${(scan.started_at || '').split('T')[0]}\n\n` +
        `--- Security Indicators (${indicators.length} found) ---\n` +
        lines.join('\n')
      );
    } catch (_) {}
  };

  // Generate D2 PCRA Report (ANS Domain 2) — async polling (same pattern as Remediation E3R)
  const generatePcraReport = async () => {
    setGeneratingPcra(true);
    setPcraProgress(0);
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        scan_id: selectedScanForPcra || null,
        workspace_id: activeWorkspaceId,
        format: pcraFormat,
        options: pcraOptions,
      };

      // Start async generation task
      const startRes = await axios.post(`/api/v1/pcra/generate`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { task_id } = startRes.data;

      // Poll until completed
      const poll = async () => {
        try {
          const statusRes = await axios.get(`/api/v1/pcra/status/${task_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const { status, progress, message, files } = statusRes.data;
          setPcraProgress(progress || 0);

          if (status === 'completed' && files && Object.keys(files).length > 0) {
            const fmt = Object.keys(files)[0];
            const dlRes = await axios.get(`/api/v1/pcra/download/${task_id}/${fmt}`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
            });
            const blob = new Blob([dlRes.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PCRA_D2_ANS_${new Date().toISOString().split('T')[0]}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setGeneratingPcra(false);
            setPcraProgress(100);
          } else if (status === 'failed') {
            throw new Error(message || 'PCRA generation failed');
          } else {
            setTimeout(poll, 3000);
          }
        } catch (pollErr) {
          console.error('PCRA poll error:', pollErr);
          setGeneratingPcra(false);
          setPcraProgress(0);
          alert(`Generation error: ${pollErr.message}`);
        }
      };
      setTimeout(poll, 2000);

    } catch (error) {
      console.error('Error starting PCRA generation:', error);
      alert(`Startup error: ${error.response?.data?.detail || error.message}`);
      setGeneratingPcra(false);
      setPcraProgress(0);
    }
  };

  // Generate ANSSI E3R Remediation Report
  const generateRemediationReport = async () => {
    if (!selectedScanForRemediation) {
      alert(t('errors.selectRequired', 'Please select a scan to generate the remediation plan'));
      return;
    }

    setGeneratingRemediation(true);
    setRemediationProgress(0);

    try {
      const token = localStorage.getItem('access_token');

      // Start generation task
      const response = await fetch('/api/v1/remediation/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scan_id: selectedScanForRemediation,
          formats: [remediationFormat],
          options: {
            language: 'fr',
            tlp_level: 'TLP:AMBER',
            include_mitre_mapping: remediationOptions.includeMitre,
            include_procedures: remediationOptions.includeProcedures,
            include_scripts: remediationOptions.includeScripts,
            include_hardening: remediationOptions.includeHardening,
            company_name: companyName || 'Organisation'
          },
          use_llm: remediationOptions.useLlm
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || t('reports.generateError', 'Error during generation'));
      }

      const data = await response.json();

      // If synchronous generation, download directly
      if (data.file_url) {
        // Direct download
        const downloadResponse = await fetch(data.file_url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `remediation_anssi_${selectedScanForRemediation}.${remediationFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Update reports count
        const currentCount = parseInt(localStorage.getItem('reports_count') || '0');
        localStorage.setItem('reports_count', (currentCount + 1).toString());
        setStats(prev => ({ ...prev, reports_generated: currentCount + 1 }));

        alert(t('success.saved', 'ANSSI E3R Remediation Plan successfully generated!'));
      } else if (data.task_id) {
        // Async generation - poll for status
        setRemediationTaskId(data.task_id);
        pollRemediationStatus(data.task_id, token);
      }

    } catch (error) {
      console.error('Error generating remediation report:', error);
      alert(`${t('reports.generateError', 'Error during generation')} : ${error.message}`);
      setGeneratingRemediation(false);
    }
  };

  // Poll remediation task status
  const pollRemediationStatus = async (taskId, token) => {
    try {
      const response = await fetch(`/api/v1/remediation/status/${taskId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setRemediationProgress(data.progress || 0);

      if (data.status === 'completed') {
        // Download the file using the download endpoint
        if (data.files && data.files[remediationFormat]) {
          try {
            const downloadResponse = await fetch(`/api/v1/remediation/download/${taskId}/${remediationFormat}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!downloadResponse.ok) {
              throw new Error('Download failed');
            }

            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `remediation_anssi_e3r_${selectedScanForRemediation}.${remediationFormat}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Update reports count
            const currentCount = parseInt(localStorage.getItem('reports_count') || '0');
            localStorage.setItem('reports_count', (currentCount + 1).toString());
            setStats(prev => ({ ...prev, reports_generated: currentCount + 1 }));

            alert(t('success.saved', 'ANSSI E3R remediation plan generated successfully! The file has been downloaded.'));
          } catch (downloadError) {
            console.error('Download error:', downloadError);
            alert(t('reports.downloadError', 'The plan was generated but the download failed. Please try again.'));
          }
        } else {
          alert(t('reports.noGeneratedFile', 'Generation complete but no file available.'));
        }
        setGeneratingRemediation(false);
        setRemediationTaskId(null);
        setRemediationProgress(0);
      } else if (data.status === 'failed') {
        alert(`${t('errors.error', 'Error')}: ${data.error || t('reports.generateError', 'Generation failed')}`);
        setGeneratingRemediation(false);
        setRemediationTaskId(null);
        setRemediationProgress(0);
      } else {
        // Continue polling
        setTimeout(() => pollRemediationStatus(taskId, token), 2000);
      }
    } catch (error) {
      console.error('Error polling remediation status:', error);
      setGeneratingRemediation(false);
    }
  };

  const generateReport = async () => {
    if (selectedScans.length === 0 || !selectedReportType) {
      alert(t('errors.selectRequired', 'Please select at least one scan and a report type'));
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token');
      const format = EXPORT_FORMATS.find(f => f.id === selectedFormat);

      // Step 1: Generate the report and get the file path
      const response = await fetch(`/api/v1/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          scan_ids: selectedScans,
          report_type: selectedReportType,
          template: selectedTemplate,
          format: selectedFormat,
          language: selectedLanguage,
          // Additional Gamma API options
          card_dimension: cardDimension,
          image_source: imageSource,
          text_mode: textMode,
          text_amount: textAmount,
          page_count: pageCount
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t('reports.generateError', 'Error during generation'));
      }

      if (selectedFormat === 'json') {
        // For JSON, display in new window
        const jsonWindow = window.open('', '_blank');
        jsonWindow.document.write('<pre>' + JSON.stringify(data, null, 2) + '</pre>');
      } else {
        // Download the generated file (PDF or PPTX)
        const fileKey = selectedFormat === 'pptx' ? 'pptx' : 'pdf';
        const filePath = data.files?.[fileKey] || data.files?.pdf;

        if (!filePath) {
          throw new Error(t('reports.noGeneratedFile', 'No file generated'));
        }

        // Extract just the filename from the path
        const filename = filePath.split('/').pop();

        // Download the file from backend
        const downloadResponse = await fetch(`/api/v1/reports/download/${filename}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!downloadResponse.ok) {
          throw new Error(`Download error: ${downloadResponse.status}`);
        }

        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_${selectedReportType}_${new Date().toISOString().split('T')[0]}${format.extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }

      // Update report count
      const newCount = parseInt(localStorage.getItem('reports_count') || '0') + 1;
      localStorage.setItem('reports_count', newCount.toString());
      setStats(prev => ({ ...prev, reports_generated: newCount }));

      // Add to history
      setGeneratedReports(prev => [{
        id: Date.now(),
        type: selectedReportType,
        format: selectedFormat,
        scans: selectedScans.length,
        generated_at: new Date().toISOString()
      }, ...prev.slice(0, 9)]);

    } catch (error) {
      console.error('Failed to generate report:', error);
      alert(`Report generation error: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Generate ALL AI Reports at once (Remediation, Compliance, Risk, Executive, Technical)
  const generateAllAIReports = async () => {
    // Get scans from workspace
    const workspaceScans = scans.filter(s => s.status === 'completed');

    if (workspaceScans.length === 0) {
      alert(t('reports.noScansAvailable', 'No completed scans available to generate reports. Please run a scan first.'));
      return;
    }

    setGeneratingAllReports(true);
    setShowBatchModal(true);

    const scanIds = workspaceScans.map(s => s.id);
    const firstScanId = scanIds[0];
    const token = localStorage.getItem('access_token');
    const orgName = companyName || 'Organisation';

    // Define all reports to generate
    const reportsToGenerate = [
      {
        id: 'scan_pdf',
        name: 'Rapport de Scan (PDF)',
        type: 'scan',
        generate: async () => {
          const response = await fetch('/api/v1/reports/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_ids: scanIds,
              report_type: 'technical',
              template: 'professional',
              format: 'pdf',
              language: 'fr'
            })
          });
          return response;
        }
      },
      {
        id: 'scan_docx',
        name: 'Rapport de Scan (Word)',
        type: 'scan',
        generate: async () => {
          const response = await fetch('/api/v1/reports/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_ids: scanIds,
              report_type: 'technical',
              template: 'professional',
              format: 'docx',
              language: 'fr'
            })
          });
          return response;
        }
      },
      {
        id: 'remediation',
        name: 'Plan de Remediation ANSSI E3R',
        type: 'remediation',
        generate: async () => {
          const response = await fetch('/api/v1/remediation/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_id: firstScanId,
              formats: ['pdf'],
              options: {
                language: 'fr',
                tlp_level: 'TLP:AMBER',
                include_mitre_mapping: true,
                include_procedures: true,
                include_scripts: true,
                include_hardening: true,
                company_name: orgName
              },
              use_llm: true
            })
          });
          return response;
        }
      },
      {
        id: 'compliance_iso',
        name: 'Plan de Conformite ISO 27001',
        type: 'compliance',
        generate: async () => {
          const response = await fetch('/api/v1/compliance/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              document_type: 'iso27001',
              format: 'pdf',
              company_name: orgName,
              scan_id: firstScanId,
              include_vulns: true
            })
          });
          return response;
        }
      },
      {
        id: 'compliance_pssi',
        name: 'Security Policy (PSSI)',
        type: 'compliance',
        generate: async () => {
          const response = await fetch('/api/v1/compliance/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              document_type: 'pssi',
              format: 'pdf',
              company_name: orgName,
              scan_id: firstScanId,
              include_vulns: true
            })
          });
          return response;
        }
      },
      {
        id: 'risk_cartography',
        name: 'Cartographie des Risques',
        type: 'risk',
        generate: async () => {
          const response = await fetch('/api/v1/reports/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_ids: scanIds,
              report_type: 'attack_surface',
              template: 'professional',
              format: 'pdf',
              language: 'fr'
            })
          });
          return response;
        }
      },
      {
        id: 'executive',
        name: 'Executive Report',
        type: 'executive',
        generate: async () => {
          const response = await fetch('/api/v1/reports/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_ids: scanIds,
              report_type: 'executive',
              template: 'professional',
              format: 'pptx',
              language: 'fr',
              card_dimension: '16x9',
              image_source: 'aiGenerated',
              text_mode: 'generate',
              text_amount: 'medium',
              page_count: 15
            })
          });
          return response;
        }
      },
      {
        id: 'technical',
        name: 'Detailed Technical Report',
        type: 'technical',
        generate: async () => {
          const response = await fetch('/api/v1/reports/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              scan_ids: scanIds,
              report_type: 'technical',
              template: 'professional',
              format: 'pptx',
              language: 'fr',
              card_dimension: '16x9',
              image_source: 'aiGenerated',
              text_mode: 'generate',
              text_amount: 'detailed',
              page_count: 30
            })
          });
          return response;
        }
      }
    ];

    setBatchProgress({ current: 0, total: reportsToGenerate.length, currentReport: '' });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < reportsToGenerate.length; i++) {
      const report = reportsToGenerate[i];
      setBatchProgress({
        current: i + 1,
        total: reportsToGenerate.length,
        currentReport: report.name
      });

      try {
        const response = await report.generate();

        if (response.ok) {
          // Just check response is OK - files are saved server-side
          if (report.type === 'compliance') {
            // Compliance returns blob directly, just consume it
            await response.blob();
          } else {
            // Other reports return JSON with file info
            await response.json();
          }
          successCount++;
        } else {
          const errorData = await response.json().catch(() => ({}));
          errors.push(`${report.name}: ${errorData.detail || 'Error'}`);
          errorCount++;
        }
      } catch (error) {
        console.error(`Error generating ${report.name}:`, error);
        errors.push(`${report.name}: ${error.message}`);
        errorCount++;
      }

      // Small delay between reports to avoid overloading
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Update reports count
    const currentCount = parseInt(localStorage.getItem('reports_count') || '0');
    localStorage.setItem('reports_count', (currentCount + successCount).toString());
    setStats(prev => ({ ...prev, reports_generated: currentCount + successCount }));

    setBatchProgress({ current: reportsToGenerate.length, total: reportsToGenerate.length, currentReport: 'Completed!' });

    setTimeout(async () => {
      setGeneratingAllReports(false);
      setShowBatchModal(false);

      // Refresh and open the reports slider
      await loadSavedReports();
      setShowReportsPanel(true);

      if (errorCount > 0) {
        alert(`Generation complete!\n${successCount} reports generated.\n${errorCount} error(s):\n${errors.join('\n')}`);
      }
    }, 1500);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="flex items-center gap-1 text-brand-green text-sm"><CheckCircle2 size={14} />Completed</span>;
      case 'running':
        return <span className="flex items-center gap-1 text-blue-400 text-sm"><Loader2 size={14} className="animate-spin" />En cours</span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-red-400 text-sm"><AlertTriangle size={14} />Failed</span>;
      default:
        return <span className="flex items-center gap-1 text-gray-400 text-sm"><Clock size={14} />{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Filter scans
  const filteredScans = scans.filter(scan =>
    !searchTerm || scan.target?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PlanRestrictedPage
      feature="report_executive"
      title={t('reports.title')}
      description="Rapports de pentest professionnels avec assistance IA"
      icon={FileText}
    >
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto">
        {/* Welcome Banner */}
        <ReportsWelcomeBanner />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gradient-green mb-2">{t('reports.generateReport')}</h1>
            <p className="text-gray-400">Customize and generate your pentest reports with AI assistance</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/mitre-navigator')}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-brand-green/20 text-brand-green hover:bg-brand-green/30 border border-brand-green/30"
            >
              <Shield className="w-4 h-4" />
              MITRE Navigator
            </button>
            <button
              onClick={openReportsPanel}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showReportsPanel ? 'bg-brand-green text-brand-black' : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30 border border-brand-green/30'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              {t('reports.title')}
            </button>
            <button
              onClick={() => setShowPromptModal(true)}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-gradient-to-r from-green-500/30 to-emerald-500/30 text-green-300 hover:from-green-500/40 hover:to-emerald-500/40 border border-green-500/30 font-semibold"
            >
              <Sparkles className="w-4 h-4" />
              {t('reports.promptIa')}
            </button>
            <button
              onClick={generateAllAIReports}
              disabled={generatingAllReports}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-300 hover:from-purple-500/40 hover:to-pink-500/40 border border-purple-500/30 font-semibold disabled:opacity-50"
            >
              {generatingAllReports ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('reports.generating')}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  {t('reports.aiReports')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Banner - Validation Required */}
        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start gap-4">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Info className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold mb-1">{t('reports.validationRequired')}</h3>
            <p className="text-sm text-gray-400">
              {t('reports.validationRequiredDesc')}
              <Link to="/scans" className="text-blue-400 hover:text-blue-300 underline ml-1">Scans</Link>,
              <Link to="/credentials" className="text-blue-400 hover:text-blue-300 underline ml-1">Identifiants</Link>,
              <Link to="/ad" className="text-blue-400 hover:text-blue-300 underline ml-1">Active Directory</Link> et
              <Link to="/attack" className="text-blue-400 hover:text-blue-300 underline ml-1">Attaques</Link>.
              {t('reports.validationRequiredDesc2')}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6 overflow-x-auto">
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_scans}</div>
                <div className="text-xs text-gray-500">{t('nav.scans')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">{t('dashboard.totalScans')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.totalScansTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-gray-600/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-600/20 flex items-center justify-center">
                <Bug className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_vulns}</div>
                <div className="text-xs text-gray-500">{t('nav.vulnerabilities')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-gray-600/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Bug className="w-4 h-4 text-gray-400" />
                <span className="font-semibold text-white">{t('nav.vulnerabilities')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.totalVulnsTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{stats.critical}</div>
                <div className="text-xs text-gray-500">{t('vulnerabilities.severities.critical')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">{t('vulnerabilities.severities.critical')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.criticalVulnsTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-orange-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-400">{stats.high}</div>
                <div className="text-xs text-gray-500">{t('vulnerabilities.severities.high')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-orange-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                <span className="font-semibold text-white">{t('vulnerabilities.severities.high')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.highVulnsTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-yellow-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Info className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-400">{stats.medium}</div>
                <div className="text-xs text-gray-500">{t('vulnerabilities.severities.medium')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-yellow-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-yellow-400" />
                <span className="font-semibold text-white">{t('vulnerabilities.severities.medium')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.mediumVulnsTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-blue-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-400">{stats.low}</div>
                <div className="text-xs text-gray-500">{t('vulnerabilities.severities.low')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">{t('vulnerabilities.severities.low')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.lowVulnsTooltip')}</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-green-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">{stats.reports_generated}</div>
                <div className="text-xs text-gray-500">{t('reports.title')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-green-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-green-400" />
                <span className="font-semibold text-white">{t('reports.totalReports')}</span>
              </div>
              <p className="text-xs text-gray-300">{t('reports.generatedReportsTooltip')}</p>
            </div>
          </div>
        </div>

        {/* Preparer la Remediation Section - ANSSI E3R */}
        <div className="mb-6 relative">
          {!hasFeature('remediation_e3r') && (
            <div className="absolute inset-0 bg-brand-black/80 backdrop-blur-sm z-10 rounded-xl flex flex-col items-center justify-center">
              <Lock className="w-8 h-8 text-amber-400 mb-2" />
              <p className="text-white font-semibold mb-1">{t('reports.enterpriseFeature')}</p>
              <p className="text-gray-400 text-sm mb-3">ANSSI E3R remediation requires the Enterprise plan</p>
              <Link to="/pricing" className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium">
                Upgrade
              </Link>
            </div>
          )}
          <div
            className="flex items-center justify-between p-4 bg-brand-black-light rounded-xl border border-amber-500/30 cursor-pointer hover:bg-brand-black-lighter transition-all"
            onClick={() => hasFeature('remediation_e3r') && setShowRemediationSection(!showRemediationSection)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Wrench className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  {t('reports.prepareRemediation')}
                  {!hasFeature('remediation_e3r') && <Lock className="w-4 h-4 text-amber-400" />}
                </h2>
                <p className="text-sm text-gray-400">ANSSI E3R Methodology - Containment, Eradication, Elimination, Reconstruction</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded flex items-center gap-1">
                <Brain className="w-3 h-3" />
                {t('reports.aiAssisted')}
              </span>
              {!hasFeature('remediation_e3r') && (
                <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">Enterprise</span>
              )}
              {showRemediationSection ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {showRemediationSection && (
            <div className="mt-4 p-6 bg-brand-black-light rounded-xl border border-amber-500/20">
              {/* ANSSI E3R Framework Info */}
              <div className="mb-6 p-4 bg-amber-500/5 rounded-lg border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2">ANSSI Methodology - Incident Response (E3R)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                          <ShieldAlert className="w-3 h-3 text-red-400" />
                        </div>
                        <div>
                          <span className="text-red-400 font-medium">Endiguement</span>
                          <p className="text-gray-500">Limiter la propagation</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Crosshair className="w-3 h-3 text-orange-400" />
                        </div>
                        <div>
                          <span className="text-orange-400 font-medium">Eviction</span>
                          <p className="text-gray-500">Eliminer la menace</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                          <Hammer className="w-3 h-3 text-yellow-400" />
                        </div>
                        <div>
                          <span className="text-yellow-400 font-medium">Eradication</span>
                          <p className="text-gray-500">Corriger les failles</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                          <RotateCcw className="w-3 h-3 text-green-400" />
                        </div>
                        <div>
                          <span className="text-green-400 font-medium">Reconstruction</span>
                          <p className="text-gray-500">Return to normal</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scan Selection */}
              <div className="mb-6">
                <label className="block text-xs text-gray-400 mb-2">
                  <Target size={12} className="inline mr-1" />
                  {t('reports.linkToScanOptional')}
                </label>
                {(() => {
                  // Get vulnerability count from the correct field
                  const getVulnCount = (scan) => scan.total_vulnerabilities || scan.vulns_count || scan.vulnerabilities_count || 0;

                  // Filter scans that have vulnerabilities (useful for remediation)
                  const scansWithVulns = allScansForRemediation.filter(s => getVulnCount(s) > 0);
                  const scansWithoutVulns = allScansForRemediation.filter(s => getVulnCount(s) === 0);

                  // Calculate total vulns across all scans
                  const totalVulnsAllScans = allScansForRemediation.reduce((sum, s) => sum + getVulnCount(s), 0);

                  return (
                    <>
                      <select
                        value={selectedScanForRemediation || ''}
                        onChange={(e) => setSelectedScanForRemediation(e.target.value)}
                        className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:outline-none text-sm"
                      >
                        <option value="">
                          {allScansForRemediation.length > 0
                            ? t('reports.selectAScan')
                            : t('reports.noScansAvailable')
                          }
                        </option>
                        {allScansForRemediation.length > 1 && totalVulnsAllScans > 0 && (
                          <optgroup label={t('reports.globalRemediation')}>
                            <option value="all_scans">
                              Tous les Scans ({allScansForRemediation.length} scans - {totalVulnsAllScans} vulns au total)
                            </option>
                          </optgroup>
                        )}
                        {scansWithVulns.length > 0 && (
                          <optgroup label={`${t('reports.scansWithVulns')} (${scansWithVulns.length})`}>
                            {scansWithVulns.map(scan => {
                              const scanName = scan.name || `Scan ${scan.target?.substring(0, 20) || 'N/A'}`;
                              const scanTarget = scan.target || 'Cible inconnue';
                              const scanDate = scan.created_at ? new Date(scan.created_at).toLocaleDateString('fr-FR') : 'N/A';
                              const vulnCount = getVulnCount(scan);
                              return (
                                <option key={scan.id} value={scan.id}>
                                  {scanName} ({scanTarget} - {scanDate} - {vulnCount} vulns)
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                        {scansWithoutVulns.length > 0 && (
                          <optgroup label={`${t('reports.scansWithoutVulns')} (${scansWithoutVulns.length})`}>
                            {scansWithoutVulns.map(scan => {
                              const scanName = scan.name || `Scan ${scan.target?.substring(0, 20) || 'N/A'}`;
                              const scanTarget = scan.target || 'Cible inconnue';
                              const scanDate = scan.created_at ? new Date(scan.created_at).toLocaleDateString('fr-FR') : 'N/A';
                              return (
                                <option key={scan.id} value={scan.id}>
                                  {scanName} ({scanTarget} - {scanDate} - 0 vulns)
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                      </select>
                      {allScansForRemediation.length === 0 && (
                        <p className="mt-2 text-xs text-yellow-500">
                          {t('reports.noScansInWorkspace')}
                        </p>
                      )}
                      {allScansForRemediation.length > 0 && totalVulnsAllScans === 0 && (
                        <p className="mt-2 text-xs text-yellow-500">
                          No vulnerabilities detected in scans. The remediation plan will be empty.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Options Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Format Selection */}
                <div>
                  <label className="block text-xs text-gray-400 mb-3">
                    <FileDown size={12} className="inline mr-1" />
                    {t('reports.outputFormat')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRemediationFormat('pdf')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        remediationFormat === 'pdf'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                          : 'bg-brand-black text-gray-400 border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <FileText size={16} />
                      PDF
                    </button>
                    <button
                      onClick={() => setRemediationFormat('xlsx')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        remediationFormat === 'xlsx'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                          : 'bg-brand-black text-gray-400 border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <Table2 size={16} />
                      Excel
                    </button>
                    <button
                      onClick={() => setRemediationFormat('docx')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        remediationFormat === 'docx'
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                          : 'bg-brand-black text-gray-400 border border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <FileCode size={16} />
                      Word
                    </button>
                  </div>
                </div>

                {/* Generation Options */}
                <div>
                  <label className="block text-xs text-gray-400 mb-3">
                    <Cog size={12} className="inline mr-1" />
                    {t('reports.generationOptions')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                      <input
                        type="checkbox"
                        checked={remediationOptions.useLlm}
                        onChange={(e) => setRemediationOptions({...remediationOptions, useLlm: e.target.checked})}
                        className="w-4 h-4 rounded bg-brand-black border-gray-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                      />
                      <Brain size={14} className="text-amber-400 flex-shrink-0" />
                      Analyse IA (LLM contextuel)
                    </label>
                    <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                      <input
                        type="checkbox"
                        checked={remediationOptions.includeMitre}
                        onChange={(e) => setRemediationOptions({...remediationOptions, includeMitre: e.target.checked})}
                        className="w-4 h-4 rounded bg-brand-black border-gray-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                      />
                      <Crosshair size={14} className="text-purple-400 flex-shrink-0" />
                      Mapping MITRE ATT&CK
                    </label>
                  </div>
                </div>
              </div>

              {/* Advanced Options */}
              <div className="mb-6 p-4 bg-brand-black rounded-lg border border-gray-700">
                <h4 className="text-xs font-semibold text-gray-400 mb-3 flex items-center gap-2">
                  <BookOpen size={12} />
                  Remediation plan content
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                    <input
                      type="checkbox"
                      checked={remediationOptions.includeProcedures}
                      onChange={(e) => setRemediationOptions({...remediationOptions, includeProcedures: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                    />
                    Detailed procedures
                  </label>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                    <input
                      type="checkbox"
                      checked={remediationOptions.includeScripts}
                      onChange={(e) => setRemediationOptions({...remediationOptions, includeScripts: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                    />
                    Remediation scripts
                  </label>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                    <input
                      type="checkbox"
                      checked={remediationOptions.includeHardening}
                      onChange={(e) => setRemediationOptions({...remediationOptions, includeHardening: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 flex-shrink-0"
                    />
                    Recommandations hardening
                  </label>
                </div>
              </div>

              {/* Progress Bar (when generating) */}
              {generatingRemediation && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                      Generating remediation plan...
                    </span>
                    <span>{remediationProgress}%</span>
                  </div>
                  <div className="w-full bg-brand-black rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-300"
                      style={{ width: `${remediationProgress}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {remediationProgress < 30 && t('reports.analyzingVulnerabilities')}
                    {remediationProgress >= 30 && remediationProgress < 60 && t('reports.generatingE3rProcedures')}
                    {remediationProgress >= 60 && remediationProgress < 90 && t('reports.aiEnrichment')}
                    {remediationProgress >= 90 && t('reports.finalizingDocument')}
                  </div>
                </div>
              )}

              {/* Generate Button */}
              <LicenseBlock feature="report generation">
              <button
                onClick={generateRemediationReport}
                disabled={!selectedScanForRemediation || generatingRemediation}
                className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  selectedScanForRemediation && !generatingRemediation
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/20'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                {generatingRemediation ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('reports.generating')}
                  </>
                ) : (
                  <>
                    <Wrench className="w-5 h-5" />
                    {t('reports.generateRemediationPlan')}
                  </>
                )}
              </button>
              </LicenseBlock>

              {/* Info Note */}
              <p className="mt-4 text-xs text-gray-500 text-center">
                {t('reports.remediationPlanDesc')}
              </p>
            </div>
          )}
        </div>

        {/* D2 - PCRA Section (ANS - Business Continuity and Recovery Strategy Domain) */}
        <div className="mb-6 relative">
          <div
            className="flex items-center justify-between p-4 bg-brand-black-light rounded-xl border border-teal-500/30 cursor-pointer hover:bg-brand-black-lighter transition-all"
            onClick={() => setShowPcraSection(!showPcraSection)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <Activity className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  {t('reports.continuityRecovery')}
                  <span className="text-xs px-1.5 py-0.5 bg-teal-500/20 text-teal-400 rounded font-normal">ANS D2</span>
                </h2>
                <p className="text-sm text-gray-400">Healthcare PCRA — BIA, Operational sheets, ISO 22301</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 bg-teal-500/20 text-teal-400 rounded flex items-center gap-1">
                <Brain className="w-3 h-3" />
                {t('reports.aiAssisted')}
              </span>
              <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Domaine 2</span>
              {showPcraSection ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </div>

          {showPcraSection && (
            <div className="mt-4 p-6 bg-brand-black-light rounded-xl border border-teal-500/20">

              {/* Framework Overview */}
              <div className="mb-6 p-4 bg-teal-500/5 rounded-lg border border-teal-500/20">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-white mb-1">
                      ANS — Domain 2: Continuity and recovery strategy
                    </h3>
                    <p className="text-xs text-gray-400 mb-4">
                      Healthcare PCRA Kit — Compliant with July 3, 2025 decree · ISO 22301 · ESSMS Sector
                    </p>

                    {/* 4 Unavailability scenarios */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-teal-400 mb-2">{t('reports.pcraDomains.heading')}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {[
                          { icon: <Server className="w-3.5 h-3.5 text-blue-400" />, label: t('reports.pcraDomains.informationSystems'), color: 'bg-blue-500/10 border-blue-500/20 text-blue-300' },
                          { icon: <Users className="w-3.5 h-3.5 text-purple-400" />, label: t('reports.pcraDomains.staffKeySkills'), color: 'bg-purple-500/10 border-purple-500/20 text-purple-300' },
                          { icon: <Building2 className="w-3.5 h-3.5 text-orange-400" />, label: t('reports.pcraDomains.buildingsInfra'), color: 'bg-orange-500/10 border-orange-500/20 text-orange-300' },
                          { icon: <Network className="w-3.5 h-3.5 text-red-400" />, label: t('reports.pcraDomains.suppliers'), color: 'bg-red-500/10 border-red-500/20 text-red-300' },
                        ].map((s, i) => (
                          <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${s.color}`}>
                            {s.icon}
                            <span className="leading-tight">{s.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 4 Phases PCRA */}
                    <div>
                      <p className="text-xs font-semibold text-teal-400 mb-2">{t('reports.pcraPhases.heading')}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {[
                          { num: '1', label: t('reports.pcraPhases.scoping'), sub: t('reports.pcraPhases.scopingSub'), color: 'text-teal-400 bg-teal-500/10 border-teal-500/20' },
                          { num: '2', label: t('reports.pcraPhases.bia'), sub: t('reports.pcraPhases.biaSub'), color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
                          { num: '3', label: t('reports.pcraPhases.development'), sub: t('reports.pcraPhases.developmentSub'), color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
                          { num: '4', label: t('reports.pcraPhases.mco'), sub: t('reports.pcraPhases.mcoSub'), color: 'text-green-400 bg-green-500/10 border-green-500/20' },
                        ].map((p, i) => (
                          <div key={i} className={`p-2 rounded-lg border ${p.color}`}>
                            <div className="font-bold text-xs mb-0.5">{t('reports.pcraPhases.phaseLabel')} {p.num} — {p.label}</div>
                            <div className="text-gray-400 text-[10px]">{p.sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scan Selection (optionnel) */}
              <div className="mb-6">
                <label className="block text-xs text-gray-400 mb-2">
                  <Target size={12} className="inline mr-1" />
                  {t('reports.referenceScanOptional')}
                </label>
                <select
                  value={selectedScanForPcra || ''}
                  onChange={(e) => setSelectedScanForPcra(e.target.value || null)}
                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-teal-500 focus:outline-none text-sm"
                >
                  <option value="">{t('reports.noScanGenericPcra')}</option>
                  {allScansForRemediation.map(scan => (
                    <option key={scan.id} value={scan.id}>
                      {scan.name || scan.target} — {new Date(scan.created_at).toLocaleDateString('fr-FR')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Format */}
                <div>
                  <label className="block text-xs text-gray-400 mb-3">
                    <FileDown size={12} className="inline mr-1" />
                    {t('reports.outputFormat')}
                  </label>
                  <div className="flex gap-2">
                    {[
                      { id: 'pdf', label: 'PDF', icon: <FileText size={15} />, cls: 'bg-red-500/20 text-red-400 border-red-500/50' },
                      { id: 'docx', label: 'Word', icon: <FileCode size={15} />, cls: 'bg-blue-500/20 text-blue-400 border-blue-500/50' },
                      { id: 'xlsx', label: 'Excel', icon: <Table2 size={15} />, cls: 'bg-green-500/20 text-green-400 border-green-500/50' },
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setPcraFormat(f.id)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          pcraFormat === f.id ? f.cls + ' border' : 'bg-brand-black text-gray-400 border border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        {f.icon} {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generation options */}
                <div>
                  <label className="block text-xs text-gray-400 mb-3">
                    <Cog size={12} className="inline mr-1" />
                    {t('reports.generationOptions')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                      <input type="checkbox" checked={pcraOptions.useLlm} onChange={e => setPcraOptions({...pcraOptions, useLlm: e.target.checked})}
                        className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                      <Brain size={14} className="text-teal-400" />
                      Analyse IA des risques
                    </label>
                    <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                      <input type="checkbox" checked={pcraOptions.includeIso22301} onChange={e => setPcraOptions({...pcraOptions, includeIso22301: e.target.checked})}
                        className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                      <ShieldCheck size={14} className="text-blue-400" />
                      Mapping ISO 22301
                    </label>
                  </div>
                </div>
              </div>

              {/* Contenu du PCRA */}
              <div className="mb-6 p-4 bg-brand-black rounded-lg border border-gray-700">
                <h4 className="text-xs font-semibold text-gray-400 mb-3 flex items-center gap-2">
                  <BookOpen size={12} />
                  Contenu du plan PCRA
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={pcraOptions.includeBia} onChange={e => setPcraOptions({...pcraOptions, includeBia: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                    BIA (Business Impact Analysis)
                  </label>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={pcraOptions.includeFichesOp} onChange={e => setPcraOptions({...pcraOptions, includeFichesOp: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                    Business operational sheets
                  </label>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={pcraOptions.includeDmia} onChange={e => setPcraOptions({...pcraOptions, includeDmia: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                    DMIA / PDMA per activity
                  </label>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={pcraOptions.includeExercices} onChange={e => setPcraOptions({...pcraOptions, includeExercices: e.target.checked})}
                      className="w-4 h-4 rounded bg-brand-black border-gray-600 text-teal-500" />
                    Plan d'exercices & REX
                  </label>
                </div>
              </div>

              {/* Criticality criteria */}
              <div className="mb-6 p-3 bg-brand-black rounded-lg border border-gray-800">
                <p className="text-xs text-gray-500 mb-2 font-semibold">Criticality evaluation criteria (D2)</p>
                <div className="flex flex-wrap gap-2">
                  {['Personnel', 'Patient / User', 'Operational', 'Legal', 'Media', 'Financial'].map(c => (
                    <span key={c} className="text-xs px-2 py-0.5 bg-teal-500/10 text-teal-300 rounded-full border border-teal-500/20">{c}</span>
                  ))}
                </div>
              </div>

              {/* Progress bar */}
              {generatingPcra && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-teal-400" />
                      {t('reports.generatingPcra')}
                    </span>
                    <span>{pcraProgress}%</span>
                  </div>
                  <div className="w-full bg-brand-black rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-300" style={{ width: `${pcraProgress}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {pcraProgress < 20 && t('reports.analyzingIsRisks')}
                    {pcraProgress >= 20 && pcraProgress < 40 && 'Construction du BIA...'}
                    {pcraProgress >= 40 && pcraProgress < 60 && t('reports.identifyingCriticalActivities')}
                    {pcraProgress >= 60 && pcraProgress < 80 && t('reports.generatingOperationalSheets')}
                    {pcraProgress >= 80 && pcraProgress < 95 && t('reports.aiEnrichmentContinuity')}
                    {pcraProgress >= 95 && 'Mise en forme du PCRA cadre ANS D2...'}
                  </div>
                </div>
              )}

              {/* Generate button */}
              <LicenseBlock feature="report generation">
              <button
                onClick={generatePcraReport}
                disabled={generatingPcra}
                className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  !generatingPcra
                    ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                {generatingPcra ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t('reports.generating')}</>
                ) : (
                  <><Activity className="w-5 h-5" /> {t('reports.generatePcra')}</>
                )}
              </button>
              </LicenseBlock>

              <p className="mt-3 text-xs text-gray-500 text-center">
                {t('reports.pcraCompliantDesc')}
              </p>
            </div>
          )}
        </div>

        {/* ── Active Directory Reports Section ─────────────────────────── */}
        <div className="mb-6">
          <div
            className="flex items-center justify-between p-4 bg-brand-black-light rounded-xl border border-indigo-500/40 cursor-pointer hover:bg-brand-black-lighter transition-all"
            onClick={() => setShowAdReportSection(!showAdReportSection)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Users className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  Rapports Active Directory
                  <span className="text-xs px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded-full font-normal">COMEX + Technique + Excel</span>
                </h2>
                <p className="text-sm text-gray-400">Analyse PASSI AD — PingCastle + ORADAD → Rapport COMEX, Rapport Technique, Plan d'action</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded flex items-center gap-1">
                <Brain className="w-3 h-3" />
                {t('reports.aiAssisted')}
              </span>
              {showAdReportSection ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </div>

          {showAdReportSection && (
            <div className="mt-4 p-6 bg-brand-black-light rounded-xl border border-indigo-500/20">

              {/* ── Existing pentests selector ── */}
              <div className="mb-5">
                <button
                  onClick={() => setShowAdImport(v => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-indigo-300 hover:text-indigo-200 mb-3"
                >
                  <Database size={14} />
                  {t('reports.importFromExistingPentest')}
                  {showAdImport ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {(adSessions.length > 0 || adPcScans.length > 0) && (
                    <span className="ml-1 px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs rounded-full">
                      {adSessions.length + adPcScans.length}
                    </span>
                  )}
                </button>

                {showAdImport && (
                  <div className="space-y-3">
                    {loadingAdSessions ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                        <Loader2 size={12} className="animate-spin" /> {t('reports.loadingPentests')}
                      </div>
                    ) : (adSessions.length === 0 && adPcScans.length === 0) ? (
                      <div className="text-xs text-gray-600 py-2 italic">
                        {t('reports.noAdPentestData')}
                      </div>
                    ) : (
                      <>
                        {/* Sessions AD Wizard */}
                        {adSessions.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                              <Shield size={11} className="text-indigo-400" /> Sessions Pentest AD ({adSessions.length})
                            </p>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                              {adSessions.map(s => (
                                <div
                                  key={s.session_id}
                                  className={`flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                                    selectedAdSource === s.session_id
                                      ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-200'
                                      : 'bg-brand-black border-gray-700 hover:border-indigo-500/30 text-gray-300'
                                  }`}
                                  onClick={() => importFromAdSession(s)}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                      s.status === 'completed' ? 'bg-green-400' :
                                      s.status === 'running' ? 'bg-yellow-400 animate-pulse' : 'bg-gray-500'
                                    }`} />
                                    <span className="font-mono truncate">{s.target_domain || 'Domaine inconnu'}</span>
                                    <span className="text-gray-500 flex-shrink-0">{s.dc_ip}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    <span className="text-gray-500">{(s.started_at || '').split('T')[0]}</span>
                                    {s.total_findings > 0 && (
                                      <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                                        {s.total_findings} findings
                                      </span>
                                    )}
                                    {selectedAdSource === s.session_id ? (
                                      <CheckCircle2 size={13} className="text-indigo-400" />
                                    ) : (
                                      <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 rounded hover:bg-indigo-500/20 border border-indigo-500/20">
                                        Importer
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* PingCastle scans */}
                        {adPcScans.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                              <Key size={11} className="text-blue-400" /> Audits PingCastle ({adPcScans.length})
                            </p>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                              {adPcScans.map(s => (
                                <div
                                  key={s.scan_id}
                                  className={`flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                                    selectedAdSource === s.scan_id
                                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-200'
                                      : 'bg-brand-black border-gray-700 hover:border-blue-500/30 text-gray-300'
                                  }`}
                                  onClick={() => importFromPcScan(s)}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                      s.status === 'completed' ? 'bg-green-400' : 'bg-gray-500'
                                    }`} />
                                    <span className="font-mono truncate">{s.domain || s.target_domain || 'Domaine'}</span>
                                    <span className="text-gray-500 flex-shrink-0">{s.dc_ip}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    <span className="text-gray-500">{(s.started_at || '').split('T')[0]}</span>
                                    {s.global_score != null && (
                                      <span className={`px-1.5 py-0.5 rounded font-medium ${
                                        s.global_score >= 70 ? 'bg-red-500/20 text-red-400' :
                                        s.global_score >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-green-500/20 text-green-400'
                                      }`}>
                                        Score {s.global_score}
                                      </span>
                                    )}
                                    {selectedAdSource === s.scan_id ? (
                                      <CheckCircle2 size={13} className="text-blue-400" />
                                    ) : (
                                      <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20 border border-blue-500/20">
                                        Importer
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Info banner */}
              <div className="mb-5 p-4 bg-indigo-500/5 rounded-lg border border-indigo-500/20">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2">{t('reports.adPentestMethodology')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      {[
                        { label: t('reports.adReportTypes.comex'), desc: t('reports.adReportTypes.comexDesc'), color: 'indigo' },
                        { label: t('reports.adReportTypes.technical'), desc: t('reports.adReportTypes.technicalDesc'), color: 'blue' },
                        { label: t('reports.adReportTypes.actionPlan'), desc: t('reports.adReportTypes.actionPlanDesc'), color: 'green' },
                        { label: t('reports.adReportTypes.aiSummary'), desc: t('reports.adReportTypes.aiSummaryDesc'), color: 'purple' },
                      ].map(item => (
                        <div key={item.label} className={`flex items-center gap-2`}>
                          <div className={`w-2 h-2 rounded-full bg-${item.color}-400`} />
                          <div>
                            <span className={`text-${item.color}-400 font-medium`}>{item.label}</span>
                            <p className="text-gray-500">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mission / Client */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('reports.adForm.missionName')}</label>
                  <input
                    type="text"
                    value={adMissionName}
                    onChange={e => setAdMissionName(e.target.value)}
                    placeholder={t('reports.adForm.missionPlaceholder')}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('reports.adForm.clientName')}</label>
                  <input
                    type="text"
                    value={adClientName}
                    onChange={e => setAdClientName(e.target.value)}
                    placeholder={t('reports.adForm.clientPlaceholder')}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              {/* PingCastle + ORADAD inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                    <Key size={11} />
                    {t('reports.adForm.pingcastleResults')}
                  </label>
                  <textarea
                    value={adPingcastleData}
                    onChange={e => setAdPingcastleData(e.target.value)}
                    placeholder={t('reports.adForm.pingcastlePlaceholder')}
                    rows={10}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:outline-none text-sm font-mono resize-y"
                  />
                  <p className="text-xs text-gray-600 mt-1">{adPingcastleData.length > 0 ? `${adPingcastleData.length} ${t('reports.adForm.charactersSuffix')}` : t('reports.adForm.optionalEnrich')}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                    <Network size={11} />
                    {t('reports.adForm.oradadResults')}
                  </label>
                  <textarea
                    value={adOradaData}
                    onChange={e => setAdOradaData(e.target.value)}
                    placeholder={t('reports.adForm.oradadPlaceholder')}
                    rows={10}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:outline-none text-sm font-mono resize-y"
                  />
                  <p className="text-xs text-gray-600 mt-1">{adOradaData.length > 0 ? `${adOradaData.length} ${t('reports.adForm.charactersSuffix')}` : t('reports.adForm.optionalEnrich')}</p>
                </div>
              </div>

              {/* Formats to generate */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-2">{t('reports.adForm.reportsToGenerate')}</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: 'comex_pdf', label: t('reports.adForm.comexPdfLabel'), icon: FileText, color: 'indigo', desc: t('reports.adForm.comexPdfDesc') },
                    { key: 'technical_pdf', label: t('reports.adForm.technicalPdfLabel'), icon: Shield, color: 'blue', desc: t('reports.adForm.technicalPdfDesc') },
                    { key: 'action_plan_excel', label: t('reports.adForm.actionPlanLabel'), icon: FileSpreadsheet, color: 'green', desc: t('reports.adForm.actionPlanDesc') },
                  ].map(({ key, label, icon: Icon, color, desc }) => (
                    <button
                      key={key}
                      onClick={() => setAdFormats(f => ({ ...f, [key]: !f[key] }))}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                        adFormats[key]
                          ? `bg-${color}-500/20 text-${color}-300 border-${color}-500/50`
                          : 'bg-brand-black text-gray-500 border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <Icon size={16} />
                      <div className="text-left">
                        <div>{label}</div>
                        <div className={`text-xs ${adFormats[key] ? `text-${color}-400/70` : 'text-gray-600'}`}>{desc}</div>
                      </div>
                      {adFormats[key] && <CheckCircle2 size={14} className={`text-${color}-400 ml-1`} />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center gap-3 mb-5">
                <button
                  onClick={() => setAdUseLlm(!adUseLlm)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-all ${
                    adUseLlm
                      ? 'bg-purple-500/20 text-purple-300 border-purple-500/50'
                      : 'bg-brand-black text-gray-500 border-gray-700'
                  }`}
                >
                  <Brain size={14} />
                  Enrichissement IA
                  {adUseLlm ? <CheckCircle2 size={12} className="text-purple-400" /> : null}
                </button>
                <span className="text-xs text-gray-600">
                  {adUseLlm ? 'The LLM will analyze data to enrich findings and summary' : 'Generation with template content (faster)'}
                </span>
              </div>

              {/* Progress bar */}
              {generatingAdReport && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-indigo-400 flex items-center gap-1">
                      <Loader2 size={12} className="animate-spin" />
                      {adMessage || t('reports.generating')}
                    </span>
                    <span className="text-xs text-gray-400">{adProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-indigo-500 transition-all duration-500"
                      style={{ width: `${adProgress}%` }}
                    />
                  </div>
                  {adProgress < 50 && adUseLlm && (
                    <p className="text-xs text-gray-600 mt-1">{t('reports.aiAnalyzingData')}</p>
                  )}
                </div>
              )}

              {/* Download links */}
              {adFiles && Object.keys(adFiles).length > 0 && (
                <div className="mb-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-sm text-green-400 font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    {t('reports.reportsGeneratedSuccessfully')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(adFiles).map(fmt => {
                      const labels = {
                        comex_pdf: { label: 'Rapport COMEX (PDF)', icon: FileText, color: 'indigo' },
                        technical_pdf: { label: 'Rapport Technique (PDF)', icon: Shield, color: 'blue' },
                        action_plan_excel: { label: 'Plan d\'action (Excel)', icon: FileSpreadsheet, color: 'green' },
                      };
                      const { label, icon: Icon, color } = labels[fmt] || { label: fmt, icon: Download, color: 'gray' };
                      return (
                        <button
                          key={fmt}
                          onClick={() => downloadAdReport(fmt)}
                          className={`flex items-center gap-2 px-4 py-2 bg-${color}-500/20 text-${color}-300 border border-${color}-500/40 rounded-lg text-sm hover:bg-${color}-500/30 transition-all`}
                        >
                          <Icon size={16} />
                          <Download size={14} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Generate button */}
              <LicenseBlock feature="les rapports Active Directory">
              <button
                onClick={generateAdReport}
                disabled={generatingAdReport}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-900 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {generatingAdReport ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t('reports.generating')} ({adProgress}%)</>
                ) : (
                  <><Users className="w-5 h-5" /> {t('reports.generateAdReports')}</>
                )}
              </button>
              </LicenseBlock>
              <p className="text-xs text-gray-600 text-center mt-2">
                PingCastle + ORADAD → COMEX PDF · Technique PDF · Plan d'action Excel — PASSI AD
              </p>
            </div>
          )}
        </div>

        {/* Plans de Conformite Section */}
        <div className="mb-6">
          <div
            className="flex items-center justify-between p-4 bg-brand-black-light rounded-xl border border-emerald-500/30 cursor-pointer hover:bg-brand-black-lighter transition-all"
            onClick={() => setShowComplianceSection(!showComplianceSection)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{t('reports.compliancePlans')}</h2>
                <p className="text-sm text-gray-400">ISO 27001, ISO 27002, SMSI, PSSI, PCA, PRA</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">6 templates</span>
              {showComplianceSection ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {showComplianceSection && (
            <div className="mt-4 p-6 bg-brand-black-light rounded-xl border border-emerald-500/20">
              {/* Compliance Types Grid */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <ClipboardList size={16} className="text-emerald-400" />
                  Type de document
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {COMPLIANCE_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <div
                        key={type.id}
                        onClick={() => setSelectedComplianceType(type.id)}
                        className={`p-4 rounded-xl cursor-pointer transition-all border ${
                          selectedComplianceType === type.id
                            ? 'border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/30'
                            : 'border-gray-700 bg-brand-black hover:border-emerald-500/50 hover:bg-brand-black-lighter'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${type.color} w-fit mb-2`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <h4 className="font-semibold text-white text-sm">{type.name}</h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{type.description}</p>
                        {selectedComplianceType === type.id && (
                          <CheckCircle2 size={16} className="text-emerald-400 mt-2" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Options Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {/* Company Name */}
                <div>
                  <label className="block text-xs text-gray-400 mb-2">
                    <Building2 size={12} className="inline mr-1" />
                    Nom de l'organisation
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Nom de votre entreprise"
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-emerald-500 focus:outline-none text-sm"
                  />
                </div>

                {/* Format */}
                <div>
                  <label className="block text-xs text-gray-400 mb-2">
                    <FileDown size={12} className="inline mr-1" />
                    Format de sortie
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setComplianceFormat('pdf')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        complianceFormat === 'pdf'
                          ? 'bg-emerald-500 text-black'
                          : 'bg-brand-black border border-gray-700 text-gray-400 hover:text-white hover:border-emerald-500/50'
                      }`}
                    >
                      PDF
                    </button>
                    <button
                      onClick={() => setComplianceFormat('docx')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        complianceFormat === 'docx'
                          ? 'bg-emerald-500 text-black'
                          : 'bg-brand-black border border-gray-700 text-gray-400 hover:text-white hover:border-emerald-500/50'
                      }`}
                    >
                      DOCX
                    </button>
                  </div>
                </div>

                {/* Optional: Link to Scan */}
                <div>
                  <label className="block text-xs text-gray-400 mb-2">
                    <Target size={12} className="inline mr-1" />
                    {t('reports.linkToScanOptional')}
                  </label>
                  <select
                    value={selectedScanForCompliance || ''}
                    onChange={(e) => setSelectedScanForCompliance(e.target.value || null)}
                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none text-sm"
                  >
                    <option value="">{t('reports.withoutScanData')}</option>
                    {scans.map(scan => (
                      <option key={scan.id} value={scan.id}>
                        {scan.target} - {new Date(scan.created_at).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Generate Button */}
                <div className="flex items-end">
                  <LicenseBlock feature="report generation" className="w-full">
                  <PlanRestrictedButton
                    feature="report_executive"
                    onClick={generateComplianceDocument}
                    disabled={!selectedComplianceType || generatingCompliance}
                    className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-black disabled:text-gray-500 font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                    disabledClassName="!bg-gray-700 !text-gray-400"
                    showLockIcon={true}
                    tooltipPosition="top"
                  >
                    {generatingCompliance ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('reports.generating')}
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        {t('reports.generateDocument')}
                      </>
                    )}
                  </PlanRestrictedButton>
                  </LicenseBlock>
                </div>
              </div>

              {/* Info */}
              <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                <p className="text-xs text-gray-400">
                  <Info size={12} className="inline mr-1 text-emerald-400" />
                  These documents are generated according to official standards. You can customize them with your organization name
                  and optionally include security scan data to enrich the content.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* AI Assistant Panel */}
        {showAIPanel && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 overflow-hidden mb-6">
            <div className="p-4 border-b border-blue-500/20 bg-blue-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-400" />
                Assistant IA - Analyse de Rapports
                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">LLM</span>
              </h3>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {aiQuickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => askAI(prompt.query)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors flex items-center gap-2 text-sm"
                  >
                    <prompt.icon className="w-4 h-4" />
                    {prompt.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiQuery && askAI(aiQuery)}
                  placeholder={t('reports.askQuestion')}
                  className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500"
                />
                <button
                  onClick={() => aiQuery && askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Analyser
                </button>
              </div>

              {aiResponse && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">Reponse IA</span>
                        {aiResponse.isLocal && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">Local</span>}
                      </div>
                      <MarkdownRenderer content={aiResponse.response} variant="compact" />
                      {aiResponse.suggestions && aiResponse.suggestions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Suggestions:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.suggestions.map((s, i) => (
                              <button key={i} onClick={() => askAI(typeof s === 'string' ? s : String(s))} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs hover:bg-blue-500/20 flex items-center gap-1">
                                <Lightbulb className="w-3 h-3" />{typeof s === 'string' ? s : String(s)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Grid - Fixed height to align with MITRE Navigator */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-stretch" style={{ minHeight: '720px' }}>
          {/* Section Gauche - Liste des Scans */}
          <div className="xl:col-span-1 flex flex-col">
            <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-hidden flex flex-col h-full">
              <div className="p-4 border-b border-brand-green/20">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Globe size={20} className="text-brand-green" />
                    Scans Valides
                  </h2>
                  <span className="text-sm text-gray-400">{filteredScans.length} scan(s)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher…"
                      className="w-full pl-9 pr-3 py-1.5 bg-brand-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-400"
                    />
                  </div>
                  <button
                    onClick={selectAllScans}
                    className="text-xs text-brand-green hover:text-brand-green-light transition-colors whitespace-nowrap"
                  >
                    {selectedScans.length === scans.length ? 'Aucun' : 'Tous'}
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-green mx-auto mb-2" />
                    <p className="text-gray-400">Chargement...</p>
                  </div>
                ) : fetchError ? (
                  <div className="p-8 text-center">
                    <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-300 mb-2">{fetchError}</p>
                    <button
                      onClick={fetchData}
                      className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {t('reports.retry')}
                    </button>
                  </div>
                ) : filteredScans.length === 0 ? (
                  <div className="p-8 text-center">
                    <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">{t('reports.noValidScans')}</p>
                    <p className="text-xs text-gray-500 mt-2">{t('reports.noValidScansDesc')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-brand-green/10 overflow-y-auto max-h-[880px]">
                    {filteredScans.map(scan => (
                      <div
                        key={scan.id}
                        onClick={() => toggleScanSelection(scan.id)}
                        className={`p-4 cursor-pointer transition-all hover:bg-brand-green/5 ${
                          selectedScans.includes(scan.id) ? 'bg-brand-green/10 border-l-4 border-brand-green' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedScans.includes(scan.id) ? 'bg-brand-green border-brand-green' : 'border-gray-600'
                          }`}>
                            {selectedScans.includes(scan.id) && <CheckCircle2 size={14} className="text-brand-black" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-white truncate">{scan.target}</h3>
                              <div className="flex items-center gap-2">
                                {scan.report_generated && (
                                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                    <FileText size={10} />
                                    Rapport
                                  </span>
                                )}
                                {getStatusBadge(scan.status)}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-400 mb-2">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDate(scan.validated_at)}
                              </span>
                            </div>
                            {scan.total_vulnerabilities > 0 && (
                              <div className="flex items-center gap-2 text-xs">
                                <Bug size={12} className="text-gray-500" />
                                <span className="text-red-400">{scan.critical_count || 0} C</span>
                                <span className="text-orange-400">{scan.high_count || 0} H</span>
                                <span className="text-yellow-400">{scan.medium_count || 0} M</span>
                                <span className="text-blue-400">{scan.low_count || 0} L</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section Centre - Types de Rapports */}
          <div className="xl:col-span-1 flex flex-col">
            <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-hidden flex flex-col h-full">
              <div className="p-4 border-b border-brand-green/20">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText size={20} className="text-brand-green" />
                  Type de Rapport
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {REPORT_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <div
                      key={type.id}
                      onClick={() => setSelectedReportType(type.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                        selectedReportType === type.id
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-transparent bg-brand-black hover:bg-brand-black-lighter'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${type.color}`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-white">{getReportTypeName(type.id)}</h3>
                            {selectedReportType === type.id && <CheckCircle2 size={18} className="text-brand-green" />}
                          </div>
                          <p className="text-xs text-brand-green mb-1">{type.subtitle}</p>
                          <p className="text-xs text-gray-400">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Langue */}
              <div className="p-4 border-t border-brand-green/20 mt-auto">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Globe size={16} className="text-brand-green" />
                  Langue
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {LANGUAGES.slice(0, 6).map(lang => (
                    <div
                      key={lang.id}
                      onClick={() => setSelectedLanguage(lang.id)}
                      className={`p-2 rounded-lg cursor-pointer transition-all text-center border ${
                        selectedLanguage === lang.id
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-transparent bg-brand-black hover:bg-brand-black-lighter'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <p className={`text-xs mt-1 ${selectedLanguage === lang.id ? 'text-brand-green' : 'text-gray-400'}`}>
                        {lang.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section Droite */}
          <div className="xl:col-span-1 flex flex-col">
            <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-hidden flex flex-col h-full">
              {/* Templates - Plus compact */}
              <div className="p-3 border-b border-brand-green/20">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Eye size={14} className="text-brand-green" />
                  Template
                </h3>
              </div>
              <div className="p-3">
                <div className="grid grid-cols-3 gap-2">
                  {DESIGN_TEMPLATES.map(template => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`cursor-pointer rounded-lg overflow-hidden transition-all border ${
                        selectedTemplate === template.id ? 'border-brand-green ring-2 ring-brand-green/30' : 'border-gray-700 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="h-16 relative" style={{ background: template.preview }}>
                        {selectedTemplate === template.id && (
                          <div className="absolute top-1.5 right-1.5">
                            <CheckCircle2 size={16} className="text-brand-green bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <div className={`p-2 ${template.dark ? 'bg-gray-800' : 'bg-brand-black'}`}>
                        <p className={`text-xs font-medium text-center ${template.dark ? 'text-gray-300' : 'text-white'}`}>{template.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Options supplementaires */}
              <div className="p-3 border-t border-brand-green/20 space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Sparkles size={14} className="text-brand-green" />
                  Options supplementaires
                </h3>

                {/* Dimensions */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <Layout size={12} /> Dimensions
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {CARD_DIMENSIONS.map(dim => (
                      <button
                        key={dim.id}
                        onClick={() => setCardDimension(dim.id)}
                        className={`px-3 py-2 text-sm rounded-lg transition-all border ${
                          cardDimension === dim.id
                            ? 'bg-brand-green text-brand-black font-semibold border-brand-green'
                            : 'bg-brand-black text-gray-400 hover:text-white border-gray-700 hover:border-brand-green/50'
                        }`}
                      >
                        {dim.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source images */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <Image size={12} /> Source images
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {IMAGE_SOURCES.map(src => (
                      <button
                        key={src.id}
                        onClick={() => setImageSource(src.id)}
                        title={src.description}
                        className={`px-2 py-2 text-xs rounded-lg transition-all border ${
                          imageSource === src.id
                            ? 'bg-brand-green text-brand-black font-semibold border-brand-green'
                            : 'bg-brand-black text-gray-400 hover:text-white border-gray-700 hover:border-brand-green/50'
                        }`}
                      >
                        {src.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mode texte */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <Type size={12} /> Mode texte
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {TEXT_MODES.map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => setTextMode(mode.id)}
                        title={mode.description}
                        className={`px-3 py-2 text-sm rounded-lg transition-all border ${
                          textMode === mode.id
                            ? 'bg-brand-green text-brand-black font-semibold border-brand-green'
                            : 'bg-brand-black text-gray-400 hover:text-white border-gray-700 hover:border-brand-green/50'
                        }`}
                      >
                        {mode.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text quantity */}
                <div>
                  <label className="text-xs text-gray-400 mb-2">Text density</label>
                  <div className="grid grid-cols-3 gap-2">
                    {TEXT_AMOUNTS.map(amt => (
                      <button
                        key={amt.id}
                        onClick={() => setTextAmount(amt.id)}
                        className={`px-3 py-2 text-sm rounded-lg transition-all border ${
                          textAmount === amt.id
                            ? 'bg-brand-green text-brand-black font-semibold border-brand-green'
                            : 'bg-brand-black text-gray-400 hover:text-white border-gray-700 hover:border-brand-green/50'
                        }`}
                      >
                        {amt.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Nombre de pages/slides */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                    <FileText size={12} /> Nombre de pages
                    {selectedReportType && (
                      <span className="ml-2 text-brand-green">
                        (max {PAGE_COUNT_OPTIONS[selectedReportType]?.max || 50} pour {PAGE_COUNT_OPTIONS[selectedReportType]?.labelKey ? t(`reports.pageCountLabels.${PAGE_COUNT_OPTIONS[selectedReportType].labelKey}`) : 'ce type'})
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.min || 5 : 5}
                      max={selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.max || 50 : 50}
                      value={pageCount}
                      onChange={(e) => setPageCount(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-brand-green"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.min || 5 : 5}
                        max={selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.max || 50 : 50}
                        value={pageCount}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 5;
                          const min = selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.min || 5 : 5;
                          const max = selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.max || 50 : 50;
                          setPageCount(Math.min(max, Math.max(min, val)));
                        }}
                        className="w-16 px-2 py-1 bg-brand-black border border-gray-700 rounded-lg text-white text-center text-sm focus:border-brand-green focus:outline-none"
                      />
                      <span className="text-xs text-gray-500">pages</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.min || 5 : 5} min</span>
                    <span>{selectedReportType ? PAGE_COUNT_OPTIONS[selectedReportType]?.max || 50 : 50} max</span>
                  </div>
                </div>
              </div>

              {/* Formats */}
              <div className="p-3 border-t border-brand-green/20">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Download size={14} className="text-brand-green" />
                  {t('reports.format')}
                </h3>
                <div className="grid grid-cols-5 gap-2">
                  {EXPORT_FORMATS.map(format => {
                    const Icon = format.icon;
                    return (
                      <div
                        key={format.id}
                        onClick={() => setSelectedFormat(format.id)}
                        className={`p-2.5 rounded-lg cursor-pointer transition-all text-center border ${
                          selectedFormat === format.id
                            ? 'border-brand-green bg-brand-green/10'
                            : 'border-gray-700 bg-brand-black hover:bg-brand-black-lighter hover:border-brand-green/50'
                        }`}
                      >
                        <Icon size={20} className={`mx-auto mb-1 ${selectedFormat === format.id ? 'text-brand-green' : 'text-gray-400'}`} />
                        <p className={`text-xs font-medium ${selectedFormat === format.id ? 'text-brand-green' : 'text-white'}`}>{getFormatName(format.id)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Spacer pour equilibrer */}
              <div className="flex-1"></div>

              {/* Resume - Section enrichie en bas */}
              <div className="p-4 border-t border-brand-green/20 bg-gradient-to-b from-brand-black/30 to-brand-black/60">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <PieChart size={16} className="text-brand-green" />
                  {t('reports.configurationSummary')}
                </h3>

                {/* Selection des scans */}
                <div className="mb-4 p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400 uppercase tracking-wide">{t('reports.selectedScansLabel')}</span>
                    <span className={`text-lg font-bold ${selectedScans.length > 0 ? 'text-brand-green' : 'text-gray-500'}`}>
                      {selectedScans.length}
                    </span>
                  </div>
                  {selectedScans.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedScans.slice(0, 3).map(scanId => {
                        const scan = scans.find(s => s.id === scanId);
                        return (
                          <span key={scanId} className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded-full truncate max-w-[120px]">
                            {scan?.target || scanId.slice(0, 8)}
                          </span>
                        );
                      })}
                      {selectedScans.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-gray-700/50 text-gray-400 rounded-full">
                          +{selectedScans.length - 3} autres
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Type de rapport */}
                <div className="mb-4 p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 uppercase tracking-wide">{t('reports.reportType')}</span>
                    {selectedReportType ? (
                      <div className="flex items-center gap-2">
                        {(() => {
                          const reportType = REPORT_TYPES.find(t => t.id === selectedReportType);
                          const Icon = reportType?.icon;
                          return (
                            <>
                              {Icon && <Icon size={14} className="text-brand-green" />}
                              <span className="text-sm font-medium text-white">{getReportTypeName(reportType?.id)}</span>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 italic">{t('reports.notSelected')}</span>
                    )}
                  </div>
                  {selectedReportType && (
                    <p className="text-xs text-gray-500 mt-1">
                      {REPORT_TYPES.find(t => t.id === selectedReportType)?.subtitle}
                    </p>
                  )}
                </div>

                {/* Grille des options */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Template */}
                  <div className="p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400">Template</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {DESIGN_TEMPLATES.find(t => t.id === selectedTemplate)?.name}
                    </span>
                  </div>

                  {/* Format */}
                  <div className="p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                    <div className="flex items-center gap-2 mb-1">
                      <FileDown size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400">{t('reports.format')}</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {getFormatName(selectedFormat)}
                    </span>
                  </div>

                  {/* Langue */}
                  <div className="p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400">Langue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{LANGUAGES.find(l => l.id === selectedLanguage)?.flag}</span>
                      <span className="text-sm font-medium text-white">
                        {LANGUAGES.find(l => l.id === selectedLanguage)?.name}
                      </span>
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Layout size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400">Dimensions</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {CARD_DIMENSIONS.find(d => d.id === cardDimension)?.name}
                    </span>
                  </div>
                </div>

                {/* Options avancees */}
                <div className="p-3 bg-brand-black/50 rounded-lg border border-brand-green/10">
                  <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Options avancees</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded font-semibold">
                      {pageCount} pages
                    </span>
                    <span className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded">
                      Images: {IMAGE_SOURCES.find(s => s.id === imageSource)?.name}
                    </span>
                    <span className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded">
                      Texte: {TEXT_MODES.find(m => m.id === textMode)?.name}
                    </span>
                    <span className="text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded">
                      Density: {TEXT_AMOUNTS.find(a => a.id === textAmount)?.name}
                    </span>
                  </div>
                </div>

                {/* Validation status */}
                <div className="mt-4 pt-3 border-t border-gray-700/30">
                  <div className="flex items-center gap-2">
                    {selectedScans.length > 0 && selectedReportType ? (
                      <>
                        <CheckCircle2 size={16} className="text-brand-green" />
                        <span className="text-sm text-brand-green font-medium">{t('reports.readyToGenerate')}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} className="text-yellow-500" />
                        <span className="text-sm text-yellow-500">
                          {selectedScans.length === 0 ? t('reports.selectScans') : t('reports.chooseReportType')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Generation Button */}
              <div className="p-3 border-t border-brand-green/20">
                <PlanRestrictedButton
                  feature="report_executive"
                  onClick={generateReport}
                  disabled={generating || selectedScans.length === 0 || !selectedReportType}
                  className={`w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                    generating || selectedScans.length === 0 || !selectedReportType
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-brand-green text-brand-black hover:bg-brand-green-dark hover:shadow-lg hover:shadow-brand-green/30'
                  }`}
                  disabledClassName="!bg-gray-700 !text-gray-400"
                  showLockIcon={true}
                  tooltipPosition="top"
                >
                  {generating ? (
                    <><Loader2 className="animate-spin" size={20} />{t('common.loading')}...</>
                  ) : (
                    <><FileDown size={20} />{t('reports.generateReport')}</>
                  )}
                </PlanRestrictedButton>

                {selectedScans.length === 0 && <p className="text-xs text-yellow-500 text-center mt-2">{t('reports.selectAtLeastOneScan')}</p>}
                {!selectedReportType && selectedScans.length > 0 && <p className="text-xs text-yellow-500 text-center mt-2">{t('reports.chooseReportType')}</p>}
              </div>

              {/* Recent Reports */}
              {generatedReports.length > 0 && (
                <div className="p-4 border-t border-gray-700">
                  <h3 className="text-sm font-bold text-white mb-2">{t('reports.recentReports')}</h3>
                  <div className="space-y-2 max-h-[100px] overflow-y-auto">
                    {generatedReports.map(report => (
                      <div key={report.id} className="flex items-center justify-between p-2 bg-brand-black rounded text-xs">
                        <span className="text-white">{getReportTypeName(report.type)}</span>
                        <span className="text-gray-500">{formatDate(report.generated_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Risk Cartography Section */}
        <div className="mb-6 relative">
          {!hasFeature('risk_cartography') && (
            <div className="absolute inset-0 bg-brand-black/80 backdrop-blur-sm z-10 rounded-xl flex flex-col items-center justify-center min-h-[200px]">
              <Lock className="w-8 h-8 text-brand-green mb-2" />
              <p className="text-white font-semibold mb-1">{t('reports.proFeature')}</p>
              <p className="text-gray-400 text-sm mb-3">Risk cartography requires the Pro plan or higher</p>
              <Link to="/pricing" className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors text-sm font-medium">
                Upgrade
              </Link>
            </div>
          )}
          <RiskCartography scanIds={selectedScans} compact={false} />
        </div>

      </div>

      {/* Slide Panel - Report list organized by type */}
      {showReportsPanel && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowReportsPanel(false)}
          />

          {/* Panel */}
          <div className="fixed right-0 top-0 h-full w-[550px] bg-brand-black-light border-l border-brand-green/30 z-50 shadow-2xl transform transition-transform duration-300 ease-in-out">
            {/* Header */}
            <div className="p-6 border-b border-brand-green/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-brand-green" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('reports.reportsLibrary')}</h2>
                  <p className="text-sm text-gray-400">{savedReports.length} {t('reports.title').toLowerCase()}{savedReports.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button
                onClick={() => setShowReportsPanel(false)}
                className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Refresh button */}
            <div className="p-4 border-b border-brand-green/20">
              <button
                onClick={loadSavedReports}
                disabled={loadingReports}
                className="w-full py-2 px-4 bg-brand-green/10 text-brand-green rounded-lg hover:bg-brand-green/20 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loadingReports ? 'animate-spin' : ''}`} />
                {t('reports.refreshList')}
              </button>
            </div>

            {/* Reports List - Organized by Categories */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              {loadingReports ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
                  <span className="ml-3 text-gray-400">{t('common.loading')}</span>
                </div>
              ) : savedReports.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg mb-2">{t('reports.noReports')}</p>
                  <p className="text-gray-500 text-sm">{t('reports.generatedReportsWillAppear')}</p>
                </div>
              ) : (
                <>
                  {/* 5 Main Categories */}
                  {(() => {
                    // Define 6 main categories matching backend types
                    const categories = [
                      {
                        id: 'remediation',
                        name: 'Prepare Remediation',
                        subtitle: 'Plans d\'action ANSSI E3R, corrections',
                        icon: Wrench,
                        color: 'green',
                        types: ['remediation', 'e3r', 'retest']
                      },
                      {
                        id: 'compliance',
                        name: 'Compliance Plans',
                        subtitle: 'ISO 27001, PSSI, RGPD, NIS2, PCI-DSS',
                        icon: ClipboardList,
                        color: 'blue',
                        types: ['compliance', 'audit']
                      },
                      {
                        id: 'continuity',
                        name: 'Continuity & Activity Recovery ANSSI D2',
                        subtitle: 'PCRA — ANS Domain 2: continuity and recovery strategy',
                        icon: Shield,
                        color: 'teal',
                        types: ['pcra', 'continuity', 'bcp', 'continuite']
                      },
                      {
                        id: 'risk_cartography',
                        name: 'Risk & Compliance Cartography',
                        subtitle: 'Attack surface, risk analysis, laws',
                        icon: ShieldAlert,
                        color: 'orange',
                        types: ['risk_cartography', 'attack_surface', 'data_leak']
                      },
                      {
                        id: 'active_directory',
                        name: 'Active Directory',
                        subtitle: 'AD audit, privileges, GPO, delegations',
                        icon: Users,
                        color: 'indigo',
                        types: ['active_directory', 'ad']
                      },
                      {
                        id: 'complete',
                        name: 'Rapports Complets',
                        subtitle: 'Rapports de scans PDF et Word',
                        icon: FileText,
                        color: 'red',
                        types: ['complete', 'technical', 'executive', 'redteam', 'other'],
                        formatsOnly: ['pdf', 'docx']
                      },
                      {
                        id: 'specific',
                        name: 'Rapports Specifiques',
                        subtitle: 'Executive, Technical, Red Team Presentations',
                        icon: Presentation,
                        color: 'purple',
                        types: ['specific', 'technical', 'executive', 'redteam'],
                        formatsOnly: ['pptx']
                      }
                    ];

                    // Color classes
                    const colorClasses = {
                      green: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30', gradient: 'from-green-500/20 to-emerald-500/20' },
                      blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', gradient: 'from-blue-500/20 to-indigo-500/20' },
                      teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-teal-500/30', gradient: 'from-teal-500/20 to-cyan-500/20' },
                      orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30', gradient: 'from-orange-500/20 to-red-500/20' },
                      indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/30', gradient: 'from-indigo-500/20 to-purple-500/20' },
                      red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', gradient: 'from-red-500/20 to-pink-500/20' },
                      purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', gradient: 'from-purple-500/20 to-pink-500/20' },
                    };

                    // Helper: workspace name from id
                    const getWorkspaceName = (wsId) => {
                      if (!wsId) return null;
                      const ws = (workspaces || []).find(w => w.id === wsId);
                      return ws ? ws.name : null;
                    };

                    // Categorize each report based on type from API
                    const categorizedReports = {};
                    categories.forEach(cat => categorizedReports[cat.id] = []);

                    savedReports.forEach(report => {
                      const reportType = (report.type || 'other').toLowerCase();
                      const fname = (report.filename || '').toLowerCase();
                      const isPptx = fname.endsWith('.pptx');

                      let assigned = false;

                      // Check all type-based categories (remediation, compliance, continuity, risk_cartography, active_directory)
                      for (const cat of categories.filter(c => !c.formatsOnly)) {
                        if (cat.types.includes(reportType) ||
                            (cat.id === 'continuity' && (fname.includes('pcra') || fname.includes('pcra_d2') || fname.includes('reprise') || fname.includes('continuite') || fname.includes('bcp')))) {
                          categorizedReports[cat.id].push(report);
                          assigned = true;
                          break;
                        }
                      }

                      // Then check complete (PDF/DOCX) vs specific (PPTX)
                      if (!assigned) {
                        if (isPptx) {
                          categorizedReports['specific'].push(report);
                        } else {
                          categorizedReports['complete'].push(report);
                        }
                      }
                    });

                    // Sort reports by date (newest first)
                    Object.keys(categorizedReports).forEach(catId => {
                      categorizedReports[catId].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                    });

                    return categories.map(cat => {
                      const reports = categorizedReports[cat.id] || [];
                      const Icon = cat.icon;
                      const colors = colorClasses[cat.color];
                      const isExpanded = expandedReportTypes[cat.id] !== false;

                      return (
                        <div key={cat.id} className={`rounded-xl border ${colors.border} overflow-hidden`}>
                          {/* Category Header */}
                          <button
                            onClick={() => setExpandedReportTypes(prev => ({ ...prev, [cat.id]: !isExpanded }))}
                            className={`w-full p-4 bg-gradient-to-r ${colors.gradient} flex items-center justify-between hover:opacity-90 transition-opacity`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2.5 rounded-xl ${colors.bg} border ${colors.border}`}>
                                <Icon className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div className="text-left">
                                <h3 className="font-bold text-white">{cat.name}</h3>
                                <p className="text-xs text-gray-400">{cat.subtitle}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                                {reports.length}
                              </span>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </button>

                          {/* Reports List */}
                          {isExpanded && reports.length > 0 && (
                            <div className="bg-brand-black/50">
                              {reports.map((report, index) => {
                                const isPdf = report.filename?.endsWith('.pdf');
                                const isDocx = report.filename?.endsWith('.docx');
                                const isPptx = report.filename?.endsWith('.pptx');
                                const isXlsx = report.filename?.endsWith('.xlsx');

                                return (
                                  <div
                                    key={report.filename || index}
                                    className="p-4 border-t border-gray-800/50 hover:bg-brand-black/80 transition-colors"
                                  >
                                    <div className="flex items-center gap-4">
                                      {/* File Icon */}
                                      <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                                        isPdf ? 'bg-red-500/20' : isDocx ? 'bg-blue-500/20' : isXlsx ? 'bg-green-500/20' : 'bg-orange-500/20'
                                      }`}>
                                        {isPdf ? (
                                          <FileText className="w-5 h-5 text-red-400" />
                                        ) : isDocx ? (
                                          <FileCode className="w-5 h-5 text-blue-400" />
                                        ) : isXlsx ? (
                                          <FileText className="w-5 h-5 text-green-400" />
                                        ) : (
                                          <Presentation className="w-5 h-5 text-orange-400" />
                                        )}
                                      </div>

                                      {/* Report Info */}
                                      <div className="flex-1 min-w-0">
                                        {/* Filename */}
                                        <h4 className="text-white font-medium text-sm truncate" title={report.filename}>
                                          {report.filename || 'Rapport'}
                                        </h4>

                                        {/* Target & Date */}
                                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                          {/* Workspace badge */}
                                          {(() => {
                                            const wsName = getWorkspaceName(report.workspace_id);
                                            return wsName ? (
                                              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-green/10 border border-brand-green/20 text-xs text-brand-green">
                                                <Layers className="w-3 h-3" />
                                                <span className="truncate max-w-[80px]">{wsName}</span>
                                              </div>
                                            ) : null;
                                          })()}

                                          {/* Target/Cible */}
                                          {report.scan_name && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                              <Target className="w-3.5 h-3.5 text-cyan-400" />
                                              <span className="truncate max-w-[120px]">{report.scan_name}</span>
                                            </div>
                                          )}

                                          {/* Date & Time */}
                                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span>{report.created_at ? formatDate(report.created_at) : '-'}</span>
                                          </div>

                                          {/* Format Badge */}
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            isPdf ? 'bg-red-500/20 text-red-400' :
                                            isDocx ? 'bg-blue-500/20 text-blue-400' :
                                            isXlsx ? 'bg-green-500/20 text-green-400' :
                                            'bg-orange-500/20 text-orange-400'
                                          }`}>
                                            {isPdf ? 'PDF' : isDocx ? 'DOCX' : isXlsx ? 'XLSX' : 'PPTX'}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Action Buttons */}
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {/* Regenerate Button */}
                                        <button
                                          onClick={() => regenerateReport(report)}
                                          disabled={regeneratingReport === (report.filename || report.id)}
                                          className="p-2.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          title={t('reports.regenerateReport')}
                                        >
                                          {regeneratingReport === (report.filename || report.id) ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                          ) : (
                                            <RotateCcw className="w-5 h-5" />
                                          )}
                                        </button>

                                        {/* Download Button */}
                                        <button
                                          onClick={() => downloadSavedReport(report.filename, isPdf ? 'pdf' : isDocx ? 'docx' : isXlsx ? 'xlsx' : 'pptx')}
                                          className="p-2.5 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
                                          title={t('reports.download')}
                                        >
                                          <Download className="w-5 h-5" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Empty State */}
                          {isExpanded && reports.length === 0 && (
                            <div className="bg-brand-black/50 p-6 text-center border-t border-gray-800/50">
                              <Icon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                              <p className="text-gray-500 text-sm">Aucun rapport dans cette categorie</p>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Batch AI Reports Generation Modal */}
      {showBatchModal && (
        <>
          <div className="fixed inset-0 bg-black/70 z-50" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-brand-black-light border border-purple-500/30 rounded-2xl p-8 max-w-lg w-full shadow-2xl">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                  <Wand2 className="w-7 h-7 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('reports.aiReportGeneration')}</h2>
                  <p className="text-gray-400 text-sm">Tous les rapports du workspace</p>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Progression</span>
                  <span className="text-sm font-bold text-purple-400">{batchProgress.current} / {batchProgress.total}</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                    style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current Report */}
              <div className="bg-brand-black rounded-xl p-4 border border-gray-800 mb-6">
                <div className="flex items-center gap-3">
                  {batchProgress.currentReport === 'Done!' ? (
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  ) : (
                    <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                  )}
                  <div>
                    <p className="text-sm text-gray-400">{t('reports.generating')}</p>
                    <p className="text-white font-semibold">{batchProgress.currentReport || t('common.loading') + '...'}</p>
                  </div>
                </div>
              </div>

              {/* Reports List */}
              <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                {[
                  { name: 'Rapport de Scan (PDF)', icon: FileText, color: 'text-red-400' },
                  { name: 'Rapport de Scan (Word)', icon: FileCode, color: 'text-blue-400' },
                  { name: 'ANSSI E3R Remediation Plan', icon: Wrench, color: 'text-green-400' },
                  { name: 'ISO 27001 Compliance Plan', icon: ClipboardList, color: 'text-blue-400' },
                  { name: 'Security Policy (PSSI)', icon: Shield, color: 'text-blue-400' },
                  { name: 'Cartographie des Risques', icon: Globe, color: 'text-orange-400' },
                  { name: 'Executive Report', icon: BarChart3, color: 'text-purple-400' },
                  { name: 'Detailed Technical Report', icon: Shield, color: 'text-cyan-400' }
                ].map((report, idx) => {
                  const Icon = report.icon;
                  const isCompleted = idx < batchProgress.current;
                  const isCurrent = idx === batchProgress.current - 1;

                  return (
                    <div
                      key={report.name}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                        isCompleted ? 'bg-green-500/10 border border-green-500/20' :
                        isCurrent ? 'bg-purple-500/10 border border-purple-500/20' :
                        'bg-gray-800/30'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : isCurrent ? (
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      ) : (
                        <Icon className={`w-4 h-4 ${report.color} opacity-50`} />
                      )}
                      <span className={`text-sm ${isCompleted ? 'text-green-400' : isCurrent ? 'text-white' : 'text-gray-500'}`}>
                        {report.name}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Info */}
              <p className="text-xs text-gray-500 text-center">
                Les rapports seront sauvegardes et accessibles dans la bibliotheque
              </p>
            </div>
          </div>
        </>
      )}
    </div>

    {/* AI Prompt Report Modal */}
    {showPromptModal && (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => { setShowPromptModal(false); setPromptResult(null); }} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-brand-black-light border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-bold text-white">{t('reports.generateWithAi')}</h3>
              </div>
              <button onClick={() => { setShowPromptModal(false); setPromptResult(null); }} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-400">
              {t('reports.aiDetectsConfig')}
            </p>

            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Ex: Generate an executive PDF report for the pentest on sylink.fr..."
              className="w-full h-24 bg-brand-black border border-purple-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60 resize-none text-sm"
            />

            {/* Quick examples */}
            <div className="flex flex-wrap gap-2">
              {[
                'Executive PDF report from last scan',
                'Export PPTX pour COMEX',
                'Detailed technical report in English',
                'List of critical vulnerabilities'
              ].map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setPromptText(example)}
                  className="px-3 py-1 text-xs bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded-full hover:bg-purple-500/20 transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>

            {/* Parsed config display */}
            {promptResult && promptResult.parsed_config && (
              <div className="bg-brand-black/50 border border-green-500/30 rounded-lg p-3 space-y-1">
                <div className="text-xs text-green-400 font-medium mb-1">{t('reports.configurationDetected')}</div>
                <div className="text-xs text-gray-300">
                  Scan : <span className="text-white">{promptResult.parsed_config.scan_target || 'Dernier scan'}</span>
                </div>
                <div className="text-xs text-gray-300">
                  Type : <span className="text-white">{promptResult.parsed_config.report_type}</span>
                </div>
                <div className="text-xs text-gray-300">
                  Format: <span className="text-white">{promptResult.parsed_config.format?.toUpperCase()}</span>
                </div>
                <div className="text-xs text-gray-300">
                  Language: <span className="text-white">{promptResult.parsed_config.language === 'fr' ? 'French' : 'English'}</span>
                </div>
              </div>
            )}

            {/* Result message */}
            {promptResult && (
              <div className={`text-sm ${promptResult.success ? 'text-green-400' : 'text-red-400'}`}>
                {promptResult.message}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowPromptModal(false); setPromptResult(null); }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('common.close')}
              </button>
              <button
                onClick={async () => {
                  if (!promptText.trim()) return;
                  setPromptLoading(true);
                  setPromptResult(null);
                  try {
                    const { reports: reportsApi } = await import('../../services/api/client');
                    const result = await reportsApi.generateFromPrompt(promptText, activeWorkspaceId);
                    setPromptResult(result);
                    if (result.success) {
                      setTimeout(() => {
                        setShowPromptModal(false);
                        setPromptResult(null);
                        setPromptText('');
                      }, 3000);
                    }
                  } catch (err) {
                    setPromptResult({ success: false, message: err.message || 'Error during generation' });
                  } finally {
                    setPromptLoading(false);
                  }
                }}
                disabled={promptLoading || !promptText.trim()}
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-sm rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {promptLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('reports.generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {t('reports.generateReport')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </>
    )}

    </PlanRestrictedPage>
  );
};

export default ReportsList;
